import { useState, useEffect, useRef } from "react";
import {
  ActionIcon, Alert, Anchor, Avatar, Badge, Box, Button, Card, Center, Checkbox, Chip, CloseButton, Container, Divider, Grid, Group,
  Loader, Menu, Modal, MultiSelect, NumberInput, Popover, Select, Stack, Switch, Table, Tabs, TagsInput, Text, Textarea, TextInput, ThemeIcon, Title, Tooltip,
  UnstyledButton,
} from "@mantine/core";
import { useIsMobile } from "../hooks/useIsMobile";
import ScrollableTabsList from "../components/ScrollableTabsList";
import FilterBar from "../components/FilterBar";
import { modals } from "@mantine/modals";
import { notifications } from "@mantine/notifications";
import {
  IconAlertTriangle, IconArrowLeft, IconArrowRight, IconBrandLinkedin, IconBriefcase, IconBuilding, IconCalendar,
  IconCheck, IconChevronDown, IconChevronUp, IconClock, IconCoin, IconDotsVertical, IconDownload, IconExternalLink,
  IconFileText, IconFolders, IconHistory, IconListCheck, IconMail, IconMapPin, IconNote, IconNotes,
  IconPencil, IconPhone, IconPhoneCall, IconPlus, IconRefresh, IconRobot, IconSchool,
  IconSearch, IconSend, IconShieldSearch, IconStar, IconStarFilled, IconTrash, IconUnlink, IconUpload, IconUser,
  IconUsers, IconWorld, IconX,
} from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import { useLocation, useSearch } from "wouter";
import { downloadFile } from "../../../Boilerplate/api";
import { Dropzone } from "@mantine/dropzone";
import { LANGUAGES, toIsoCode, languageDisplayLabel } from "../constants/languages";
import {
  getCandidate, getCandidateFieldSources, getActivities, createActivity, updateActivity, deleteActivity,
  getCandidateSources, enrichCandidate, updateCandidate, setCandidateWatchlist,
  setCandidateSourcedBy, searchCandidates, deleteCandidate,
  getTagDefinitions, removeWebFindings, getCandidatesByCompany, getCandidateProjects, getCandidateClientLinks,
  generateSummary, submitTranscript, getTranscriptAnalysis, confirmProjectLink,
  getProjects, refetchLinkedIn, addProjectCandidate,
  confirmLinkedInIdentity, uploadCandidatePhoto, deleteCandidatePhoto,
  uploadCandidateSource, deleteCandidateSource,
} from "../api";
import CompanyAutocomplete from "../components/CompanyAutocomplete";
import GeoLocationAutocomplete from "../components/GeoLocationAutocomplete";
import ContactBadge from "../components/ContactBadge";
import InfoHint from "../components/InfoHint";
import ConsentSection from "../Consent/ConsentSection";
import TasksPanel from "../Tasks/TasksPanel";
import BackgroundCheckPanel from "../BackgroundCheck/BackgroundCheckPanel";
import { CandidateAvatar } from "./CandidateAvatar";
import { linkedInSlugMismatch } from "./linkedInSlugHint";
import { CompanyLogo } from "./CompanyLogo";
import ExpandableTags from "./ExpandableTags";
import { useStore } from "../store";
import { formatCandidateName } from "../_Shared/candidateName";
import { candidateRoleAndCompany, careerDate, isCurrentAtCompany } from "../_Shared/candidateRole";
import { candidateLocation } from "../_Shared/candidateLocation";
import { enrichmentStatusLabel, enrichmentStatusColor, creditPrice } from "../_Shared/enrichment";
import { useEnrichmentCostBasis } from "../hooks/useEnrichmentCostBasis";
import EnrichmentSteps from "../components/EnrichmentSteps";
import ProvenanceBadge from "../components/ProvenanceBadge";
import EntryHistory from "../components/EntryHistory";
import { entryTypeConfig, fieldLabel, ActivityBody, isSystemEvent } from "../_Shared/activity";

const roleColors = { candidate: "blue", client: "green", reference: "violet", source: "orange", ident: "cyan", interestingPotential: "yellow" };
const allRoles = ["candidate", "client", "reference", "source", "ident", "interestingPotential"];
const statusColors = { candidate: "blue", ident: "cyan" };

const stageOrder = { identified: 0, longlist: 1, shortlist: 2, interview: 3, finalist: 4, placed: 5, rejected: 6 };
const stageColors = { identified: "gray", longlist: "gray", shortlist: "blue", interview: "violet", finalist: "green", placed: "teal", rejected: "red" };
const isActiveStage = (stage) => stage !== "rejected" && stage !== "placed";

// entryTypeConfig / fieldLabel / activityBodyText moved to _Shared/activity.jsx (shared with the
// tenant activity feed). Imported above.

function formatDate(dateStr) {
  if (!dateStr) return "—";
  const d = new Date(dateStr);
  return d.toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" });
}

// Same DD.MM.YYYY as formatDate, plus HH:MM in the viewer's local time. createdAt is a UTC
// instant (…Z), so toLocaleString renders it local automatically. Shown inline on timeline rows
// when the "show events" toggle is on, so edits made on the same day can be ordered.
function formatDateTimeInline(dateStr) {
  if (!dateStr) return "—";
  return new Date(dateStr).toLocaleString("de-DE", {
    day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit",
  });
}

// Full localized date + time with seconds, for the hover tooltip — resolves edits made in the
// same minute (audit precision). Follows i18n.language, matching EntryHistory's timestamp style.
function formatDateTimeFull(dateStr, locale = "de-DE") {
  if (!dateStr) return "—";
  return new Date(dateStr).toLocaleString(locale, { dateStyle: "long", timeStyle: "medium" });
}

/** Returns phone numbers sorted: mobile, personal mobile, office, then home */
function sortedPhones(candidate) {
  const phones = candidate.phoneNumbers ?? [];
  if (!phones.length) return [];
  const order = { mobile: 0, mobilePrivate: 1, office: 2, home: 3 };
  return [...phones].sort((a, b) => (order[a.type] ?? 9) - (order[b.type] ?? 9));
}

/** Returns emails sorted: primary first, then business, then personal */
function sortedEmails(candidate) {
  const emails = candidate.emails ?? [];
  if (!emails.length) return [];
  return [...emails].sort((a, b) => {
    if (a.isPrimary !== b.isPrimary) return a.isPrimary ? -1 : 1;
    const order = { business: 0, personal: 1 };
    return (order[a.label] ?? 9) - (order[b.label] ?? 9);
  });
}

/** Returns postal addresses sorted: primary first, then business, personal, other. */
function sortedAddresses(candidate) {
  const addresses = candidate.addresses ?? [];
  if (!addresses.length) return [];
  return [...addresses].sort((a, b) => {
    if (a.isPrimary !== b.isPrimary) return a.isPrimary ? -1 : 1;
    const order = { business: 0, personal: 1, other: 2 };
    return (order[a.label] ?? 9) - (order[b.label] ?? 9);
  });
}

/** One-line formatting of a structured postal address: "Line1, Line2, Postal City, Region, Country". */
function formatAddress(a) {
  const cityLine = [a.postalCode, a.city].filter(Boolean).join(" ");
  return [a.line1, a.line2, cityLine, a.region, a.countryCode].filter(Boolean).join(", ");
}

/**
 * Compact contact block for the candidate header: one icon per channel, the values joined
 * inline (primary first), the type carried as a hover title rather than a coloured pill —
 * the old per-row badges truncated ("GESCHÄF…") and crowded the card. `compact` = mobile.
 */
function ContactLines({ candidate, compact }) {
  const { t } = useTranslation("Veft");
  const emails = sortedEmails(candidate);
  const phones = sortedPhones(candidate);
  const addresses = sortedAddresses(candidate);
  const fallbackEmails = !emails.length && candidate.email ? [{ address: candidate.email, label: "business" }] : [];
  const fallbackPhones = !phones.length && candidate.phone ? [{ number: candidate.phone, type: "office" }] : [];
  const allEmails = emails.length ? emails : fallbackEmails;
  const allPhones = phones.length ? phones : fallbackPhones;
  if (!allEmails.length && !allPhones.length && !addresses.length) return null;

  const iconSize = compact ? 12 : 14;
  const tiSize = compact ? "xs" : "sm";
  const justify = compact ? "flex-start" : "flex-end";
  const sep = (i) => (i > 0 ? <Text key={`s${i}`} size="xs" c="dimmed" span>·</Text> : null);

  return (
    <Stack gap={compact ? 4 : 6} align={compact ? undefined : "flex-end"} style={compact ? undefined : { flexShrink: 0 }}>
      {allEmails.length > 0 && (
        <Group gap={6} wrap="wrap" justify={justify} align="center">
          <ThemeIcon variant="light" size={tiSize} color="gray"><IconMail size={iconSize} /></ThemeIcon>
          {allEmails.map((e, i) => [
            sep(i),
            <Anchor key={`e${i}`} href={`mailto:${e.address}`} size="xs" title={t(`email_${e.label}`)}>{e.address}</Anchor>,
          ])}
        </Group>
      )}
      {allPhones.length > 0 && (
        <Group gap={6} wrap="wrap" justify={justify} align="center">
          <ThemeIcon variant="light" size={tiSize} color="gray"><IconPhone size={iconSize} /></ThemeIcon>
          {allPhones.map((p, i) => [
            sep(i),
            <Anchor key={`p${i}`} href={`tel:${p.number}`} size="xs" title={t(`phone_${p.type}`)}
              style={{ fontVariantNumeric: "tabular-nums" }}>{p.number}</Anchor>,
          ])}
        </Group>
      )}
      {addresses.map((a, i) => (
        <Group key={`ad${i}`} gap={6} wrap="nowrap" justify={justify} align="flex-start">
          <ThemeIcon variant="light" size={tiSize} color="gray"><IconMapPin size={iconSize} /></ThemeIcon>
          <Text size="xs" ta={compact ? undefined : "right"} title={t(`address_${a.label}`)}>{formatAddress(a)}</Text>
        </Group>
      ))}
    </Stack>
  );
}

function linkifySummary(text) {
  // Escape HTML — including quotes — before injecting via dangerouslySetInnerHTML. The text
  // can be import-sourced contact notes or AI output. Escaping " and ' closes the attribute-
  // breakout XSS where a URL containing a quote would otherwise escape the href="…" and inject
  // an event handler (e.g. http://x"onmouseover="alert(1)). & must be escaped first.
  const escaped = text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
  return escaped.replace(
    /(https?:\/\/[^\s<]+)/g,
    '<a href="$1" target="_blank" rel="noopener noreferrer" style="color:var(--mantine-color-blue-6)">$1</a>',
  );
}

function TimelineEntry({ entry, candidateId, onUpdated, onDeleted, showTime }) {
  const { t, i18n } = useTranslation("Veft");
  const [, setLocation] = useLocation();
  const config = entryTypeConfig[entry.type] || entryTypeConfig.note;
  const Icon = config.icon;
  const [editing, setEditing] = useState(false);
  const [editContent, setEditContent] = useState(entry.content);
  const [editType, setEditType] = useState(entry.type);
  const [saving, setSaving] = useState(false);

  // Stage transitions, system events, and field-change records are immutable audit records — no edit/delete.
  const isImmutable = entry.type === "system" || entry.type === "statusChange" || entry.type === "fieldChange";

  // Stage transitions and field changes render from structured fields (not free-text content) so
  // labels translate at render time, with field changes shown as a colored diff. Shared with the
  // tenant feed via ActivityBody.

  async function handleSave() {
    if (!editContent.trim()) return;
    setSaving(true);
    try {
      const updated = await updateActivity(candidateId, entry.id, {
        type: editType,
        content: editContent.trim(),
      });
      setEditing(false);
      onUpdated?.(updated);
    } catch (e) {
      notifications.show({ color: "red", title: t("error"), message: t("error") });
    } finally {
      setSaving(false);
    }
  }

  function handleDelete() {
    modals.openConfirmModal({
      title: t("delete_activity"),
      children: <Text size="sm">{t("delete_activity_confirm")}</Text>,
      labels: { confirm: t("delete"), cancel: t("cancel") },
      confirmProps: { color: "red" },
      onConfirm: async () => {
        try {
          await deleteActivity(candidateId, entry.id);
          onDeleted?.(entry.id);
        } catch (e) {
          notifications.show({ color: "red", title: t("error"), message: t("error") });
        }
      },
    });
  }

  return (
    <div style={{ display: "flex", gap: "var(--mantine-spacing-sm)", paddingBottom: "var(--mantine-spacing-md)" }}>
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", flexShrink: 0, width: 32 }}>
        <ThemeIcon variant="light" color={config.color} size="md" radius="xl">
          <Icon size={14} />
        </ThemeIcon>
        <div style={{ flex: 1, width: 2, background: "var(--mantine-color-gray-2)", marginTop: 4 }} />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <Group gap="xs" mb={4}>
          <Text size="xs" fw={600} c={`${config.color}.6`}>{t(config.tKey)}</Text>
          <Tooltip label={formatDateTimeFull(entry.createdAt, i18n.language)} withArrow>
            <Text size="xs" c="dimmed" style={{ cursor: "default" }}>
              {showTime ? formatDateTimeInline(entry.createdAt) : formatDate(entry.createdAt)}
            </Text>
          </Tooltip>
          <Group gap={4} ml="auto">
            {!isImmutable && (
              <>
                <ActionIcon size="xs" variant="subtle" color="gray" onClick={() => { setEditing(true); setEditContent(entry.content); setEditType(entry.type); }}>
                  <IconPencil size={12} />
                </ActionIcon>
                <ActionIcon size="xs" variant="subtle" color="red" onClick={handleDelete}>
                  <IconTrash size={12} />
                </ActionIcon>
              </>
            )}
          </Group>
        </Group>
        {editing ? (
          <Stack gap="xs">
            <Textarea autosize minRows={2} maxRows={8} value={editContent} onChange={(e) => setEditContent(e.currentTarget.value)} />
            <Group gap="xs">
              <Select size="xs" value={editType} onChange={setEditType} allowDeselect={false}
                data={Object.entries(entryTypeConfig).filter(([k]) => k !== "fieldChange").map(([k, v]) => ({ value: k, label: t(v.tKey) }))} />
              <Button size="xs" loading={saving} disabled={!editContent.trim()} onClick={handleSave}>{t("save")}</Button>
              <Button size="xs" variant="subtle" color="gray" onClick={() => setEditing(false)}>{t("cancel")}</Button>
            </Group>
          </Stack>
        ) : (
          <>
            <Text size="sm" style={{ whiteSpace: "pre-wrap" }}><ActivityBody entry={entry} t={t} /></Text>
            {entry.projectId && (
              <Anchor
                size="xs"
                c="dimmed"
                onClick={(e) => { e.preventDefault(); setLocation(`/projects/${entry.projectId}`); }}
                href={`/projects/${entry.projectId}`}
              >
                {entry.projectTitle || entry.projectId}
              </Anchor>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function QuickAdd({ candidateId, onCreated }) {
  const { t } = useTranslation("Veft");
  const draftKey = `veft:note-draft:${candidateId}`;
  const [text, setText] = useState("");
  const [type, setType] = useState("note");
  const [expanded, setExpanded] = useState(false);
  const [saving, setSaving] = useState(false);
  const skipPersist = useRef(true);

  useEffect(() => {
    skipPersist.current = true;
    try {
      const saved = localStorage.getItem(draftKey);
      if (saved) {
        setText(saved);
        setExpanded(true);
      } else {
        setText("");
        setExpanded(false);
      }
    } catch { /* private mode */ }
  }, [draftKey]);

  useEffect(() => {
    if (skipPersist.current) {
      skipPersist.current = false;
      return;
    }
    try {
      if (text.trim()) localStorage.setItem(draftKey, text);
      else localStorage.removeItem(draftKey);
    } catch { /* private mode */ }
  }, [text, draftKey]);

  useEffect(() => {
    if (!text.trim()) return undefined;
    const onBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [text]);

  async function handleSave() {
    if (!text.trim()) return;
    setSaving(true);
    try {
      const activity = await createActivity(candidateId, {
        type,
        content: text.trim(),
      });
      setText("");
      setType("note");
      setExpanded(false);
      try { localStorage.removeItem(draftKey); } catch { /* private mode */ }
      onCreated?.(activity);
    } catch (e) {
      notifications.show({ color: "red", title: t("error"), message: t("error") });
    } finally {
      setSaving(false);
    }
  }

  return (
    <Card p="sm" mb="md">
      <Textarea
        placeholder={t("add_note_placeholder")}
        autosize
        minRows={1}
        maxRows={6}
        value={text}
        onChange={(e) => {
          setText(e.currentTarget.value);
          if (!expanded && e.currentTarget.value.length > 0) setExpanded(true);
        }}
        onFocus={() => text.length > 0 && setExpanded(true)}
      />
      {expanded && (
        <Group mt="xs" justify="space-between">
          <Group gap="xs">
            <Select
              size="xs"
              value={type}
              onChange={setType}
              allowDeselect={false}
              data={Object.entries(entryTypeConfig).filter(([k]) => k !== "fieldChange").map(([k, v]) => ({ value: k, label: t(v.tKey) }))}
            />
          </Group>
          <Button
            size="xs"
            leftSection={<IconSend size={14} />}
            disabled={!text.trim()}
            loading={saving}
            onClick={handleSave}
          >
            {t("save")}
          </Button>
        </Group>
      )}
    </Card>
  );
}

const webFindingTKeys = {
  board: "finding_board", press: "finding_press", publication: "finding_publications",
  award: "finding_awards", conference: "finding_conferences", web: "finding_other",
};

function parseWebFindings(sources) {
  const webSources = sources.filter((s) => s.sourceType === "webSearch" && s.rawContent);
  const findings = [];
  for (const src of webSources) {
    try {
      const parsed = JSON.parse(src.rawContent);
      if (parsed?.sources) findings.push(...parsed.sources.map((f) => ({ ...f, _sourceId: src.id })));
    } catch { /* skip unparseable */ }
  }
  // Group by sourceType
  const grouped = {};
  for (const f of findings) {
    const key = f.sourceType || "web";
    if (!grouped[key]) grouped[key] = [];
    grouped[key].push(f);
  }
  return grouped;
}

function WebFindingsCard({ sources, candidate, candidateId, onSourcesChanged, onEnrich }) {
  const { t } = useTranslation("Veft");
  const grouped = parseWebFindings(sources);
  const categories = Object.keys(grouped);

  // Empty state: explain that web search is part of enrichment (not a separate action) and offer
  // to run it. The disambiguation gate needs a title/company/location, so disable the CTA + say so
  // when none is present. Replaces the previous silent return-null, which left users guessing.
  if (categories.length === 0) {
    const hasContext = Boolean(
      candidate?.title || candidate?.career?.[0]?.companyName || candidate?.location);
    return (
      <Card>
        <Title order={5} mb="sm">
          <Group gap="xs"><IconWorld size={16} /> {t("web_findings")}</Group>
        </Title>
        <Stack gap="xs">
          <Text size="sm" c="dimmed">{t("web_findings_empty")}</Text>
          <Text size="xs" c="dimmed">
            {hasContext ? t("web_findings_empty_help") : t("web_findings_needs_context")}
          </Text>
          {onEnrich && (
            <Button size="xs" variant="light" leftSection={<IconWorld size={14} />}
              onClick={onEnrich} disabled={!hasContext} style={{ alignSelf: "flex-start" }}>
              {t("enrich_incl_web")}
            </Button>
          )}
        </Stack>
      </Card>
    );
  }

  async function handleRemove(finding) {
    try {
      await removeWebFindings(candidateId, finding._sourceId, [finding.url]);
      onSourcesChanged?.();
    } catch {
      notifications.show({ title: t("error"), message: t("error"), color: "red" });
    }
  }

  return (
    <Card>
      <Title order={5} mb="sm">
        <Group gap="xs"><IconWorld size={16} /> {t("web_findings")}</Group>
      </Title>
      <Stack gap="sm">
        {categories.map((cat) => (
          <div key={cat}>
            <Text size="xs" fw={600} c="dimmed" mb={4}>{t(webFindingTKeys[cat] || "finding_other")}</Text>
            <Stack gap={4}>
              {grouped[cat].map((f, i) => (
                <Group key={i} gap="xs" wrap="nowrap" align="flex-start">
                  <IconExternalLink size={12} style={{ flexShrink: 0, marginTop: 3 }} color="var(--mantine-color-dimmed)" />
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <Anchor href={f.url} target="_blank" size="sm" fw={500} style={{ wordBreak: "break-word" }}>
                      {f.title}
                    </Anchor>
                    <Text size="xs" c="dimmed">{f.snippet}</Text>
                  </div>
                  <ActionIcon size="xs" variant="subtle" color="gray" onClick={() => handleRemove(f)}>
                    <IconX size={12} />
                  </ActionIcon>
                </Group>
              ))}
            </Stack>
          </div>
        ))}
      </Stack>
    </Card>
  );
}

function LanguagesEditor({ candidate, onUpdated }) {
  const { t, i18n } = useTranslation("Veft");
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);

  // Normalize existing values to ISO codes and dedupe — legacy data may hold both a name and
  // its translation ("German" + "Deutsch"), which collapse to the same code. Dedup here fixes
  // the doubled badge (and key collision) regardless of whether storage is normalized yet.
  const currentCodes = [...new Set((candidate.languages ?? []).map(toIsoCode))];

  async function handleChange(codes) {
    setSaving(true);
    try {
      const updated = await updateCandidate(candidate.id, { languages: codes });
      onUpdated(updated);
    } catch {
      notifications.show({ title: t("error"), message: t("error"), color: "red" });
    } finally {
      setSaving(false);
    }
  }

  return (
    <div>
      <Group gap="xs" align="flex-start">
        <Text size="sm" fw={500} style={{ flexShrink: 0, paddingTop: editing ? 6 : 0 }}>{t("languages")}:</Text>
        {editing ? (
          <div style={{ flex: 1 }}>
            <MultiSelect
              size="xs"
              data={LANGUAGES}
              value={currentCodes}
              onChange={handleChange}
              searchable
              placeholder={t("select_languages")}
              disabled={saving}
            />
            <Button size="xs" variant="subtle" color="gray" mt={4} onClick={() => setEditing(false)}>
              {t("done")}
            </Button>
          </div>
        ) : (
          <>
            <Group gap={4} style={{ flex: 1 }}>
              {currentCodes.length > 0
                ? currentCodes.map((code) => (
                    <Badge key={code} size="xs" variant="light">{languageDisplayLabel(code, i18n.language)}</Badge>
                  ))
                : <Text size="sm" c="dimmed">—</Text>}
            </Group>
            <ActionIcon size="xs" variant="subtle" color="gray" onClick={() => setEditing(true)}>
              <IconPencil size={12} />
            </ActionIcon>
          </>
        )}
      </Group>
    </div>
  );
}

// Compensation: read-only card that becomes a small editor. The structured figures are normally
// AI-extracted (from a note, transcript, or comp document), but a recruiter is often just *told* a
// number — this lets them record it directly. A manual entry is marked IsManual server-side so a
// later re-enrichment won't wipe it, and any document-parsed breakdown (components) is preserved
// through a top-line edit. The card renders even when empty, so there's always a place to add comp.
function CompensationEditor({ candidate, onUpdated }) {
  const { t } = useTranslation("Veft");
  const comp = candidate.lastKnownCompensation;
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [base, setBase] = useState("");
  const [total, setTotal] = useState("");
  const [currency, setCurrency] = useState("");
  const [note, setNote] = useState("");

  function startEdit() {
    setBase(comp?.baseSalary ?? "");
    setTotal(comp?.totalCompensation ?? "");
    setCurrency(comp?.currency ?? "");
    setNote(comp?.note ?? "");
    setEditing(true);
  }

  async function persist(payload) {
    setSaving(true);
    try {
      const updated = await updateCandidate(candidate.id, { lastKnownCompensation: payload });
      onUpdated(updated);
      setEditing(false);
    } catch {
      notifications.show({ title: t("error"), message: t("error"), color: "red" });
    } finally {
      setSaving(false);
    }
  }

  const save = () => persist({
    baseSalary: base === "" || base == null ? null : Number(base),
    totalCompensation: total === "" || total == null ? null : Number(total),
    currency: currency.trim() || null,
    note: note.trim() || null,
  });

  // All-empty is the server's "clear the box" signal.
  const remove = () => persist({ baseSalary: null, totalCompensation: null, currency: null, note: null });

  const hasComponents = comp?.components?.length > 0;
  const money = (n, ccy) => `${Number(n).toLocaleString("de-DE")}${ccy ? ` ${ccy}` : ""}`;

  return (
    <Card mt="md">
      <Group justify="space-between" mb="sm">
        <Title order={5}>
          <Group gap="xs"><IconCoin size={16} /> {t("compensation")}</Group>
        </Title>
        {!editing && comp && (
          <ActionIcon size="xs" variant="subtle" color="gray" onClick={startEdit} aria-label={t("edit")}>
            <IconPencil size={12} />
          </ActionIcon>
        )}
      </Group>

      {editing ? (
        <Stack gap="xs">
          <Group grow align="flex-start">
            <NumberInput size="xs" label={t("comp_base")} value={base} onChange={setBase}
              thousandSeparator="." decimalSeparator="," min={0} allowNegative={false} hideControls
              placeholder="—" disabled={saving} />
            <NumberInput size="xs" label={t("comp_total")} value={total} onChange={setTotal}
              thousandSeparator="." decimalSeparator="," min={0} allowNegative={false} hideControls
              placeholder="—" disabled={saving} />
            <TextInput size="xs" label={t("comp_currency")} value={currency} maxLength={3}
              onChange={(e) => setCurrency(e.currentTarget.value.toUpperCase())} placeholder="EUR" disabled={saving} />
          </Group>
          <Textarea size="xs" label={t("comp_note")} value={note} autosize minRows={1}
            onChange={(e) => setNote(e.currentTarget.value)} placeholder={t("comp_note_placeholder")} disabled={saving} />
          {hasComponents && <Text size="xs" c="dimmed">{t("comp_breakdown_preserved")}</Text>}
          <Group justify="space-between" mt={4}>
            {comp
              ? <Button size="xs" color="red" variant="subtle" onClick={remove} disabled={saving}>{t("comp_remove")}</Button>
              : <span />}
            <Group gap="xs">
              <Button size="xs" variant="subtle" color="gray" onClick={() => setEditing(false)} disabled={saving}>{t("cancel")}</Button>
              <Button size="xs" onClick={save} loading={saving}>{t("save")}</Button>
            </Group>
          </Group>
        </Stack>
      ) : comp ? (
        <Stack gap="xs">
          {comp.totalCompensation != null && (
            <Group gap="xs">
              <Text size="sm" fw={500}>{t("comp_total")}</Text>
              <Text size="sm">{money(comp.totalCompensation, comp.currency)}</Text>
            </Group>
          )}
          {comp.baseSalary != null && (
            <Group gap="xs">
              <Text size="sm" fw={500}>{t("comp_base")}</Text>
              <Text size="sm">{money(comp.baseSalary, comp.currency)}</Text>
            </Group>
          )}
          {hasComponents && (
            <>
              <Divider label={t("comp_breakdown")} labelPosition="left" mt={4} />
              <Stack gap={4}>
                {comp.components.map((c, i) => {
                  const ccy = c.currency || comp.currency;
                  const amount = c.amount != null ? Number(c.amount).toLocaleString("de-DE") : null;
                  return (
                    <Group key={i} gap="xs" wrap="nowrap" align="baseline">
                      <Badge size="xs" variant="light" color="gray">
                        {t(`comp_kind_${c.kind}`, { defaultValue: c.kind })}
                      </Badge>
                      <Text size="sm">
                        {c.label ? `${c.label}` : ""}
                        {c.label && (amount || c.percentage != null) ? ": " : ""}
                        {amount ? `${amount}${ccy ? ` ${ccy}` : ""}` : ""}
                        {c.period && c.period !== "unknown" && amount
                          ? ` / ${t(`comp_period_${c.period}`, { defaultValue: c.period })}` : ""}
                        {c.percentage != null ? `${amount ? " · " : ""}${c.percentage}%` : ""}
                      </Text>
                      {c.condition && <Text size="xs" c="dimmed">({c.condition})</Text>}
                    </Group>
                  );
                })}
              </Stack>
            </>
          )}
          {comp.note && (
            <Group gap="xs">
              <Text size="sm" fw={500}>{t("comp_note")}</Text>
              <Text size="sm" c="dimmed">{comp.note}</Text>
            </Group>
          )}
          {comp.asOf && (
            <Text size="xs" c="dimmed">
              {t("comp_as_of", { date: formatDate(comp.asOf) })}
              {comp.isManual ? ` · ${t("comp_manual")}` : ""}
            </Text>
          )}
        </Stack>
      ) : (
        <Button size="xs" variant="light" leftSection={<IconPlus size={14} />} onClick={startEdit}>
          {t("comp_add")}
        </Button>
      )}
    </Card>
  );
}

// Shared column widths so the three contact editors (email / phone / address) line up: the field
// label and the type chip each occupy a fixed column, so every value starts at the same x —
// "Anschriften:" no longer dictates the indent and the block reads as one calm grid.
const CONTACT_LABEL_W = 104;
const CONTACT_TYPE_W = 104;

// "Primary" is a ranking accent, decoupled from the type-colour palette: a filled amber star marks
// the primary entry; an outline star on the others promotes on click. Replaces the old far-right
// "Primär" badge / "Als primär setzen" link that scattered the trailing controls.
function PrimaryStar({ isPrimary, onSet, t }) {
  if (isPrimary) {
    return (
      <Tooltip label={t("primary") || "Primary"} withArrow>
        <ActionIcon size="sm" variant="subtle" color="brandAmber" aria-label={t("primary") || "Primary"} style={{ cursor: "default" }}>
          <IconStarFilled size={14} />
        </ActionIcon>
      </Tooltip>
    );
  }
  return (
    <Tooltip label={t("make_primary") || "Make primary"} withArrow>
      <ActionIcon size="sm" variant="subtle" color="gray" onClick={onSet} aria-label={t("make_primary") || "Make primary"}>
        <IconStar size={14} />
      </ActionIcon>
    </Tooltip>
  );
}

// The label/type two-column grid (above) only earns its keep on a wide screen. On a phone the fixed
// 104px label column plus the 104px type-chip column swallow most of the width, so long emails
// truncate mid-address and phone numbers wrap into stubs. On mobile the label moves to its own line
// above and the values reclaim the full width; the type chip shrinks to its natural size.
function ContactSection({ label, isMobile, children }) {
  if (isMobile) {
    return (
      <Stack gap={4}>
        <Text size="sm" fw={500}>{label}</Text>
        <Stack gap={6} style={{ minWidth: 0 }}>{children}</Stack>
      </Stack>
    );
  }
  return (
    <Group gap="md" align="flex-start" wrap="nowrap">
      <Text size="sm" fw={500} w={CONTACT_LABEL_W} style={{ flexShrink: 0 }}>{label}</Text>
      <Stack gap={6} style={{ flex: 1, minWidth: 0 }}>{children}</Stack>
    </Group>
  );
}

function PhoneNumbersEditor({ candidate, onUpdated }) {
  const { t } = useTranslation("Veft");
  const isMobile = useIsMobile();
  const phones = candidate.phoneNumbers ?? [];
  const [adding, setAdding] = useState(false);
  const [newNumber, setNewNumber] = useState("");
  const [newType, setNewType] = useState("mobile");

  async function addPhone() {
    if (!newNumber.trim()) return;
    const entry = { number: newNumber.trim(), type: newType, isPrimary: phones.length === 0 };
    try {
      const updated = await updateCandidate(candidate.id, { phoneNumbers: [...phones, entry] });
      onUpdated(updated);
      setNewNumber("");
      setAdding(false);
    } catch {
      notifications.show({ title: t("error"), message: t("error"), color: "red" });
    }
  }

  async function removePhone(idx) {
    try {
      const updated = await updateCandidate(candidate.id, { phoneNumbers: phones.filter((_, i) => i !== idx) });
      onUpdated(updated);
    } catch {
      notifications.show({ title: t("error"), message: t("error"), color: "red" });
    }
  }

  const sorted = sortedPhones(candidate);

  return (
    <ContactSection label={t("phone_label")} isMobile={isMobile}>
        {sorted.length > 0
          ? sorted.map((p, i) => (
              <Group gap="xs" key={i} wrap="nowrap" align="center">
                <Box w={isMobile ? undefined : CONTACT_TYPE_W} style={{ flexShrink: 0 }}>
                  <ContactBadge kind={p.type || "mobile"}>{t(`phone_${p.type || "mobile"}`)}</ContactBadge>
                </Box>
                <Anchor href={`tel:${p.number}`} size="sm" style={{ minWidth: 0, fontVariantNumeric: "tabular-nums" }}>{p.number}</Anchor>
                <CloseButton size="sm" variant="subtle" color="gray" onClick={() => removePhone(phones.indexOf(p))} aria-label={t("remove") || "Remove"} />
              </Group>
            ))
          : candidate.phone && <Text size="sm" c="dimmed">{candidate.phone}</Text>}
        {adding ? (
          <Group gap="xs" wrap={isMobile ? "wrap" : "nowrap"}>
            <TextInput placeholder={t("phone_number")} size="xs" value={newNumber}
              onChange={(e) => setNewNumber(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") addPhone(); }}
              style={{ flex: 1 }} />
            <Select size="xs" value={newType} onChange={setNewType} w={140}
              data={[
                { value: "mobile", label: t("phone_mobile") },
                { value: "mobilePrivate", label: t("phone_mobilePrivate") },
                { value: "office", label: t("phone_office") },
                { value: "home", label: t("phone_home") },
              ]} />
            <Button size="xs" variant="light" onClick={addPhone} disabled={!newNumber.trim()}>{t("add")}</Button>
            <Button size="xs" variant="subtle" color="gray" onClick={() => setAdding(false)}>{t("cancel")}</Button>
          </Group>
        ) : (
          <Button variant="subtle" size="compact-xs" leftSection={<IconPlus size={12} />} onClick={() => setAdding(true)} style={{ alignSelf: "flex-start" }}>
            {t("add_phone")}
          </Button>
        )}
    </ContactSection>
  );
}

// Client-side echo of the backend EmailNormalizer (Veft.Models/EmailNormalizer.cs): strip an Outlook
// `mailto:` hyperlink prefix / `Display Name <addr>` wrapper and trim, so a pasted value reads clean
// immediately. The backend remains authoritative — this is UX feedback, not enforcement.
function cleanEmailInput(raw) {
  if (!raw) return "";
  let v = raw.trim();
  const lt = v.lastIndexOf("<");
  if (lt >= 0) {
    const gt = v.indexOf(">", lt + 1);
    if (gt > lt) v = v.slice(lt + 1, gt).trim();
  }
  if (/^mailto:/i.test(v)) v = v.slice("mailto:".length).trim();
  const q = v.indexOf("?");
  if (q >= 0) v = v.slice(0, q).trim();
  return v;
}

// Permissive shape check mirroring the backend regex — for inline gating only.
const isEmailLike = (addr) => /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(addr);

function EmailsEditor({ candidate, onUpdated }) {
  const { t } = useTranslation("Veft");
  const isMobile = useIsMobile();
  const emails = candidate.emails ?? [];
  const [adding, setAdding] = useState(false);
  const [newAddress, setNewAddress] = useState("");
  const [newLabel, setNewLabel] = useState("business");

  async function addEmail() {
    const address = cleanEmailInput(newAddress);
    if (!isEmailLike(address)) return;
    const entry = { address, label: newLabel, isPrimary: emails.length === 0 };
    try {
      const updated = await updateCandidate(candidate.id, { emails: [...emails, entry] });
      onUpdated(updated);
      setNewAddress("");
      setAdding(false);
    } catch {
      notifications.show({ title: t("error"), message: t("error"), color: "red" });
    }
  }

  async function removeEmail(idx) {
    try {
      const next = emails.filter((_, i) => i !== idx);
      // If we removed the primary, promote the first remaining to primary.
      if (next.length > 0 && !next.some((e) => e.isPrimary)) {
        next[0] = { ...next[0], isPrimary: true };
      }
      const updated = await updateCandidate(candidate.id, { emails: next });
      onUpdated(updated);
    } catch {
      notifications.show({ title: t("error"), message: t("error"), color: "red" });
    }
  }

  async function makePrimary(idx) {
    try {
      const next = emails.map((e, i) => ({ ...e, isPrimary: i === idx }));
      const updated = await updateCandidate(candidate.id, { emails: next });
      onUpdated(updated);
    } catch {
      notifications.show({ title: t("error"), message: t("error"), color: "red" });
    }
  }

  const sorted = sortedEmails(candidate);

  return (
    <ContactSection label={t("email_label")} isMobile={isMobile}>
        {sorted.length > 0
          ? sorted.map((e) => {
              const idx = emails.indexOf(e);
              return (
                <Group gap="xs" key={idx} wrap="nowrap" align="center">
                  <Box w={isMobile ? undefined : CONTACT_TYPE_W} style={{ flexShrink: 0 }}>
                    <ContactBadge kind={e.label || "business"}>{t(`email_${e.label || "business"}`)}</ContactBadge>
                  </Box>
                  <Anchor href={`mailto:${e.address}`} size="sm" style={{ minWidth: 0, overflowWrap: "anywhere" }}>{e.address}</Anchor>
                  <PrimaryStar isPrimary={e.isPrimary} onSet={() => makePrimary(idx)} t={t} />
                  <CloseButton size="sm" variant="subtle" color="gray" onClick={() => removeEmail(idx)} aria-label={t("remove") || "Remove"} />
                </Group>
              );
            })
          : candidate.email && <Text size="sm" c="dimmed">{candidate.email}</Text>}
        {adding ? (
          <Group gap="xs" wrap={isMobile ? "wrap" : "nowrap"}>
            <TextInput placeholder={t("email_address")} size="xs" value={newAddress}
              onChange={(ev) => setNewAddress(ev.target.value)}
              onKeyDown={(ev) => { if (ev.key === "Enter") addEmail(); }}
              style={{ flex: 1 }} />
            <Select size="xs" value={newLabel} onChange={setNewLabel} w={110}
              data={[
                { value: "business", label: t("email_business") },
                { value: "personal", label: t("email_personal") },
              ]} />
            <Button size="xs" variant="light" onClick={addEmail} disabled={!isEmailLike(cleanEmailInput(newAddress))}>{t("add")}</Button>
            <Button size="xs" variant="subtle" color="gray" onClick={() => setAdding(false)}>{t("cancel")}</Button>
          </Group>
        ) : (
          <Button variant="subtle" size="compact-xs" leftSection={<IconPlus size={12} />} onClick={() => setAdding(true)} style={{ alignSelf: "flex-start" }}>
            {t("add_email")}
          </Button>
        )}
    </ContactSection>
  );
}

const emptyAddressDraft = () => ({ label: "business", line1: "", line2: "", postalCode: "", city: "", region: "", countryCode: "" });

// Structured postal addresses (business / private / other) — DISTINCT from the single free-text
// `location` field above. These mirror the email/phone editors but carry full street/postal/city/
// country components. Most candidates have only a `location`; addresses arrive from Outlook imports.
function AddressesEditor({ candidate, onUpdated }) {
  const { t } = useTranslation("Veft");
  const isMobile = useIsMobile();
  const addresses = candidate.addresses ?? [];
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState(emptyAddressDraft);

  async function save(next) {
    try {
      const updated = await updateCandidate(candidate.id, { addresses: next });
      onUpdated(updated);
      return true;
    } catch {
      notifications.show({ title: t("error"), message: t("error"), color: "red" });
      return false;
    }
  }

  async function addAddress() {
    const entry = {
      label: draft.label,
      line1: draft.line1.trim() || null,
      line2: draft.line2.trim() || null,
      postalCode: draft.postalCode.trim() || null,
      city: draft.city.trim() || null,
      region: draft.region.trim() || null,
      countryCode: draft.countryCode.trim() || null,
      isPrimary: addresses.length === 0,
    };
    if (!entry.line1 && !entry.city && !entry.postalCode) return; // require something meaningful
    if (await save([...addresses, entry])) {
      setDraft(emptyAddressDraft());
      setAdding(false);
    }
  }

  async function removeAddress(idx) {
    const next = addresses.filter((_, i) => i !== idx);
    if (next.length > 0 && !next.some((a) => a.isPrimary)) next[0] = { ...next[0], isPrimary: true };
    await save(next);
  }

  async function makePrimary(idx) {
    await save(addresses.map((a, i) => ({ ...a, isPrimary: i === idx })));
  }

  const sorted = sortedAddresses(candidate);

  return (
    <ContactSection label={t("addresses_label")} isMobile={isMobile}>
        {sorted.length > 0 &&
          sorted.map((a) => {
            const idx = addresses.indexOf(a);
            return (
              <Group gap="xs" key={idx} align="flex-start" wrap="nowrap">
                <Box w={isMobile ? undefined : CONTACT_TYPE_W} style={{ flexShrink: 0 }}>
                  <ContactBadge kind={a.label || "business"}>{t(`address_${a.label || "business"}`)}</ContactBadge>
                </Box>
                <Text size="sm" style={{ minWidth: 0, overflowWrap: "anywhere" }}>{formatAddress(a)}</Text>
                <PrimaryStar isPrimary={a.isPrimary} onSet={() => makePrimary(idx)} t={t} />
                <CloseButton size="sm" variant="subtle" color="gray" onClick={() => removeAddress(idx)} aria-label={t("remove") || "Remove"} />
              </Group>
            );
          })}
        {adding ? (
          <Stack gap={6}>
          <Select size="xs" value={draft.label} onChange={(v) => setDraft((d) => ({ ...d, label: v }))} w={130}
            data={[
              { value: "business", label: t("address_business") },
              { value: "personal", label: t("address_personal") },
              { value: "other", label: t("address_other") },
            ]} />
          <TextInput size="xs" placeholder={t("address_line1")} value={draft.line1}
            onChange={(e) => setDraft((d) => ({ ...d, line1: e.target.value }))} />
          <TextInput size="xs" placeholder={t("address_line2")} value={draft.line2}
            onChange={(e) => setDraft((d) => ({ ...d, line2: e.target.value }))} />
          <Group gap="xs" grow>
            <TextInput size="xs" placeholder={t("address_postal_code")} value={draft.postalCode}
              onChange={(e) => setDraft((d) => ({ ...d, postalCode: e.target.value }))} />
            <TextInput size="xs" placeholder={t("address_city")} value={draft.city}
              onChange={(e) => setDraft((d) => ({ ...d, city: e.target.value }))} />
          </Group>
          <Group gap="xs" grow>
            <TextInput size="xs" placeholder={t("address_region")} value={draft.region}
              onChange={(e) => setDraft((d) => ({ ...d, region: e.target.value }))} />
            <TextInput size="xs" placeholder={t("address_country")} value={draft.countryCode}
              onChange={(e) => setDraft((d) => ({ ...d, countryCode: e.target.value }))} />
          </Group>
          <Group gap="xs">
            <Button size="xs" variant="light" onClick={addAddress}>{t("add")}</Button>
            <Button size="xs" variant="subtle" color="gray" onClick={() => { setAdding(false); setDraft(emptyAddressDraft()); }}>{t("cancel")}</Button>
          </Group>
          </Stack>
        ) : (
          <Button variant="subtle" size="compact-xs" leftSection={<IconPlus size={12} />} onClick={() => setAdding(true)} style={{ alignSelf: "flex-start" }}>
            {t("add_address")}
          </Button>
        )}
    </ContactSection>
  );
}

function ColleaguesCard({ candidate }) {
  const { t } = useTranslation("Veft");
  const [colleagues, setColleagues] = useState(null);
  const [, setLocation] = useLocation();
  const nameFormat = useStore((s) => s.nameFormat);

  const currentEntry = candidate.career?.[0];
  const companyId = currentEntry?.companyId;
  const companyName = currentEntry?.companyName;

  useEffect(() => {
    if (!companyId) return;
    getCandidatesByCompany(companyId)
      .then((results) => setColleagues(results.filter((c) => c.id !== candidate.id)))
      .catch(() => setColleagues([]));
  }, [companyId, candidate.id]);

  if (!companyId || colleagues === null || colleagues.length === 0) return null;

  // The by-company endpoint matches ANY career entry, so ex-employees show up
  // too — split them out instead of presenting alumni as current co-workers.
  const current = colleagues.filter((c) => isCurrentAtCompany(c, companyId));
  const former = colleagues.filter((c) => !isCurrentAtCompany(c, companyId));

  // wrap="nowrap" + minWidth: 0 keep long names/titles wrapping inside their own
  // column; the default wrapping Group dropped the whole text block under the avatar.
  const row = (c) => (
    <Group key={c.id} gap="sm" wrap="nowrap" style={{ cursor: "pointer" }} onClick={() => setLocation(`/candidates/${c.id}`)}>
      <CandidateAvatar candidate={c} size="sm" radius="xl" color="blue" style={{ flexShrink: 0 }} />
      <div style={{ minWidth: 0 }}>
        <Text size="sm" fw={500}>{formatCandidateName(c, nameFormat)}</Text>
        {c.title && <Text size="xs" c="dimmed">{c.title}</Text>}
      </div>
    </Group>
  );

  return (
    <Card mt="md">
      <Title order={5} mb="md">{t("colleagues_at", { company: companyName })}</Title>
      <Stack gap="xs">
        {current.map(row)}
        {former.length > 0 && (
          <>
            <Text size="xs" c="dimmed" fw={600} tt="uppercase" mt={current.length > 0 ? "xs" : 0}>
              {t("colleagues_former")}
            </Text>
            {former.map(row)}
          </>
        )}
      </Stack>
    </Card>
  );
}

// The checkbox no longer decides WHETHER the fetched career lands — it always does, since a
// LinkedIn fetch is by definition fresher than what's stored. It decides whether manual edits and
// deletions are discarded along with the rest, which is why the dialog is no longer blocked on it.
function RefetchLinkedInModalBody({ onConfirm }) {
  const { t } = useTranslation("Veft");
  const [overwrite, setOverwrite] = useState(false);

  return (
    <Stack gap="sm">
      <Text size="sm">{t("refetch_linkedin_explanation")}</Text>
      <Checkbox
        label={t("overwrite_career_education")}
        description={t("overwrite_career_education_hint")}
        checked={overwrite}
        onChange={(e) => setOverwrite(e.currentTarget.checked)}
      />
      <Group justify="flex-end" gap="xs" mt="xs">
        <Button variant="subtle" color="gray" onClick={() => modals.closeAll()}>
          {t("cancel")}
        </Button>
        <Button onClick={() => { modals.closeAll(); onConfirm(overwrite); }}>
          {t("refetch_linkedin")}
        </Button>
      </Group>
    </Stack>
  );
}

function EditLinkedInUrlModalBody({ initial, candidate, onSave }) {
  const { t } = useTranslation("Veft");
  const [url, setUrl] = useState(initial);
  const [saving, setSaving] = useState(false);

  // Cheap tripwire against a mis-pasted URL: warn (never block — slugs can be maiden names or
  // nicknames) when the vanity slug plainly names someone else. Encoded /in/ACoAA… ids are
  // unreadable and skipped here; those are caught server-side by the fetch-time identity gate.
  const slugName = candidate ? linkedInSlugMismatch(url, candidate) : null;

  async function save() {
    setSaving(true);
    try {
      await onSave(url.trim());
      modals.closeAll();
    } catch {
      notifications.show({ title: t("error"), message: t("error"), color: "red" });
    } finally {
      setSaving(false);
    }
  }

  return (
    <Stack gap="sm">
      <Text size="sm">{t("linkedin_edit_url_hint")}</Text>
      <TextInput
        label={t("linkedin_url")}
        placeholder="https://www.linkedin.com/in/…"
        value={url}
        onChange={(e) => setUrl(e.currentTarget.value)}
        data-autofocus
      />
      {slugName && (
        <Alert color="yellow" icon={<IconAlertTriangle size={16} />}>
          <Text size="sm">
            {t("linkedin_slug_warning", { slugName, candidateName: candidate.name })}
          </Text>
        </Alert>
      )}
      <Group justify="flex-end" gap="xs" mt="xs">
        <Button variant="subtle" color="gray" onClick={() => modals.closeAll()}>
          {t("cancel")}
        </Button>
        <Button loading={saving} onClick={save}>{t("save")}</Button>
      </Group>
    </Stack>
  );
}

/**
 * Header avatar with photo management: click opens upload / remove. Upload stores a manual photo
 * (manual-photo-* blob) that LinkedIn sync never overwrites; remove empties the slot — the next
 * LinkedIn fetch may re-fill it with the profile's current picture, which is deliberate.
 */
function EditableCandidateAvatar({ candidate, onUpdated, ...avatarProps }) {
  const { t } = useTranslation("Veft");
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);

  async function handleFile(e) {
    const file = e.currentTarget.files?.[0];
    e.currentTarget.value = "";
    if (!file) return;
    setBusy(true);
    try {
      const updated = await uploadCandidatePhoto(candidate.id, file);
      onUpdated(updated);
      notifications.show({ title: t("photo_updated"), message: t("photo_updated_msg"), color: "green" });
    } catch {
      notifications.show({ title: t("error"), message: t("error"), color: "red" });
    } finally {
      setBusy(false);
    }
  }

  async function removePhoto() {
    setBusy(true);
    try {
      const updated = await deleteCandidatePhoto(candidate.id);
      onUpdated(updated);
      notifications.show({ title: t("photo_removed"), message: t("photo_removed_msg"), color: "blue" });
    } catch {
      notifications.show({ title: t("error"), message: t("error"), color: "red" });
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <input type="file" accept="image/jpeg,image/png,image/webp" hidden ref={fileRef} onChange={handleFile} />
      <Menu position="bottom-start" withinPortal disabled={busy}>
        <Menu.Target>
          <Tooltip label={t("photo_edit")} withArrow openDelay={500}>
            <UnstyledButton aria-label={t("photo_edit")} style={{ borderRadius: "50%" }}>
              <CandidateAvatar candidate={candidate} {...avatarProps} />
            </UnstyledButton>
          </Tooltip>
        </Menu.Target>
        <Menu.Dropdown>
          <Menu.Item leftSection={<IconUpload size={14} />} onClick={() => fileRef.current?.click()}>
            {t("photo_upload")}
          </Menu.Item>
          {candidate.photoBlobId && (
            <Menu.Item color="red" leftSection={<IconTrash size={14} />} onClick={removePhoto}>
              {t("photo_remove")}
            </Menu.Item>
          )}
        </Menu.Dropdown>
      </Menu>
    </>
  );
}

/**
 * Standalone tab for the imported contact-card notes. These hold the most sensitive CRM
 * material (compensation, candid call notes), so they live on their own tab — off-screen
 * until the user opens it — rather than under Career where they read as job history.
 */
// The raw contact-card note blob (imported from Outlook, mined once into structured data + segmented
// Note activities). It's an import ARTIFACT, not a co-equal editable notes surface — so it's shown
// read-only and collapsed at the origin of the timeline, not on its own editable tab. New notes are
// added as attributed timeline entries (QuickAdd). Nothing here writes; editing is intentionally gone.
function ImportedNoteCard({ notes }) {
  const { t } = useTranslation("Veft");
  const [open, setOpen] = useState(false);
  if (!notes) return null;
  return (
    <Card withBorder mt="md" p="sm" bg="var(--mantine-color-gray-0)">
      <Group justify="space-between" wrap="nowrap" style={{ cursor: "pointer" }} onClick={() => setOpen((o) => !o)}>
        <Group gap="xs" wrap="nowrap" style={{ minWidth: 0 }}>
          <IconNotes size={14} style={{ flexShrink: 0 }} />
          <Text size="sm" fw={500} truncate>{t("imported_note_outlook")}</Text>
          <Badge size="xs" variant="light" color="gray" style={{ flexShrink: 0 }}>{t("read_only")}</Badge>
        </Group>
        <ActionIcon size="sm" variant="subtle" color="gray" style={{ flexShrink: 0 }}
          aria-label={t("imported_note_outlook")}>
          {open ? <IconChevronUp size={14} /> : <IconChevronDown size={14} />}
        </ActionIcon>
      </Group>
      {open && (
        <Text size="sm" mt="xs" style={{ whiteSpace: "pre-wrap" }}
          dangerouslySetInnerHTML={{ __html: linkifySummary(notes) }} />
      )}
    </Card>
  );
}

function CareerTab({ candidate, activities, sources, onUpdated, onRefresh }) {
  const { t, i18n } = useTranslation("Veft");
  const [, setLocation] = useLocation();
  const costBasis = useEnrichmentCostBasis();
  const profile = useStore((s) => s.profile);
  const isAdmin = profile?.role === "GlobalAdmin" || profile?.role === "TenantOwner" || profile?.role === "TenantAdmin";
  const [editingIdx, setEditingIdx] = useState(null);
  const [editingEduIdx, setEditingEduIdx] = useState(null);
  const [draft, setDraft] = useState(null);
  const [eduDraft, setEduDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [refetching, setRefetching] = useState(false);

  // Skills state (moved from former Summary tab)
  const [showAllSkills, setShowAllSkills] = useState(false);
  const [skillInput, setSkillInput] = useState("");
  const [addingSkill, setAddingSkill] = useState(false);

  // Summary text state (moved from former Summary tab)
  const [editingSummary, setEditingSummary] = useState(false);
  const [summaryDraft, setSummaryDraft] = useState("");
  const [savingSummary, setSavingSummary] = useState(false);
  const [generatingSummary, setGeneratingSummary] = useState(false);

  // Provenance ([CV]/[LinkedIn]/[Manual]/…) — a read of the FieldChange log. `fields` = per-field
  // (scalars + section fallback); `entries` = per career/education entry. Refetched when the
  // candidate changes (id or updatedAt) so badges stay current after edits/enrichment.
  const [fieldSources, setFieldSources] = useState({ fields: [], entries: [] });
  useEffect(() => {
    if (!candidate?.id) return;
    let cancelled = false;
    getCandidateFieldSources(candidate.id)
      .then((fs) => { if (!cancelled) setFieldSources({ fields: fs?.fields ?? [], entries: fs?.entries ?? [] }); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [candidate?.id, candidate?.updatedAt]);

  // Provenance for one career/education entry: its own per-entry source, falling back to the
  // section-level source (e.g. a role LinkedIn populated but no one has individually touched).
  const entrySource = (field, entryId) =>
    fieldSources.entries.find((e) => e.fieldName === field && e.entryId === entryId)?.sourceKind
    ?? fieldSources.fields.find((f) => f.fieldName === field)?.sourceKind;

  const career = candidate.career ?? [];
  const education = candidate.education ?? [];
  const skills = candidate.skills ?? [];
  const visibleSkills = showAllSkills ? skills : skills.slice(0, 8);
  const hiddenCount = skills.length - 8;

  async function addSkill() {
    const name = skillInput.trim();
    if (!name || skills.includes(name)) return;
    setAddingSkill(true);
    try {
      const updated = await updateCandidate(candidate.id, { skills: [...skills, name] });
      onUpdated(updated);
      setSkillInput("");
    } catch {
      notifications.show({ title: t("error"), message: t("error"), color: "red" });
    } finally {
      setAddingSkill(false);
    }
  }

  async function removeSkill(name) {
    try {
      const updated = await updateCandidate(candidate.id, { skills: skills.filter((s) => s !== name) });
      onUpdated(updated);
    } catch {
      notifications.show({ title: t("error"), message: t("error"), color: "red" });
    }
  }

  async function saveSummary() {
    setSavingSummary(true);
    try {
      const updated = await updateCandidate(candidate.id, { summary: summaryDraft || null });
      onUpdated(updated);
      setEditingSummary(false);
    } catch {
      notifications.show({ title: t("error"), message: t("error"), color: "red" });
    } finally {
      setSavingSummary(false);
    }
  }

  async function handleGenerateSummary() {
    setGeneratingSummary(true);
    try {
      const updated = await generateSummary(candidate.id);
      onUpdated(updated);
      notifications.show({ title: t("summary_generated"), message: t("summary_generated_msg") });
    } catch {
      notifications.show({ title: t("error"), message: t("error"), color: "red" });
    } finally {
      setGeneratingSummary(false);
    }
  }
  const enrichmentStatus = candidate.enrichmentStatus;
  const enrichInProgress = enrichmentStatus &&
    enrichmentStatus !== "none" &&
    enrichmentStatus !== "complete" &&
    enrichmentStatus !== "failed";

  // The LinkedIn link was confirmed invalid (hard 404). Only meaningful when a
  // URL exists. Independent of career length — a link can rot AFTER a good import,
  // leaving stale career entries, so we surface this regardless of `career`.
  const linkInvalid = candidate.linkedInUrlDead === true && !!candidate.linkedInUrl;

  // Sidebar shortcut: re-run enrichment straight from the metadata panel (the failed / not-started
  // states). Mirrors the Documents-tab Re-enrich, minus the overwrite toggle.
  function reEnrichFromSidebar() {
    modals.openConfirmModal({
      title: t("reenrich_candidate"),
      centered: true,
      children: (
        <Stack gap="sm">
          <Text size="sm">{t("reenrich_explanation")}</Text>
          {costBasis && (
            <Text size="sm" c="dimmed">
              {t("reenrich_cost", {
                credits: costBasis.fullEnrichmentEstimate,
                price: creditPrice(costBasis, costBasis.fullEnrichmentEstimate, i18n.language),
              })}
            </Text>
          )}
        </Stack>
      ),
      labels: { confirm: t("re_enrich"), cancel: t("cancel") },
      onConfirm: async () => {
        try {
          await enrichCandidate(candidate.id, {});
          notifications.show({ title: t("enrichment_queued"), message: t("enrichment_queued_msg"), color: "blue" });
          onRefresh?.();
        } catch {
          notifications.show({ title: t("error"), message: t("error"), color: "red" });
        }
      },
    });
  }

  async function submitRefetch(forceOverwriteCareerEducation) {
    setRefetching(true);
    try {
      await refetchLinkedIn(candidate.id, { forceOverwriteCareerEducation });
      notifications.show({
        title: t("linkedin_refetch_queued"),
        message: t("linkedin_refetch_queued_msg"),
        color: "blue",
      });
      onRefresh?.();
    } catch {
      notifications.show({ title: t("error"), message: t("error"), color: "red" });
    } finally {
      setRefetching(false);
    }
  }

  function openRefetchModal() {
    modals.open({
      title: t("refetch_linkedin"),
      centered: true,
      children: <RefetchLinkedInModalBody onConfirm={submitRefetch} />,
    });
  }

  // "Try anyway" from the invalid-link alert: re-fetch even though the link is
  // flagged dead. forceDeadLink clears the flag server-side and re-evaluates the
  // URL; if it 404s again the fetch re-sets the flag.
  async function tryRefetchAnyway() {
    setRefetching(true);
    try {
      await refetchLinkedIn(candidate.id, { forceDeadLink: true });
      notifications.show({
        title: t("linkedin_refetch_queued"),
        message: t("linkedin_refetch_queued_msg"),
        color: "blue",
      });
      onRefresh?.();
    } catch {
      notifications.show({ title: t("error"), message: t("error"), color: "red" });
    } finally {
      setRefetching(false);
    }
  }

  // Resolve a held identity mismatch. Accept re-fetches with the name gate bypassed (the held
  // payload was deliberately discarded, so this buys one more Apify run); reject removes the URL.
  async function resolveIdentityMismatch(accept) {
    setRefetching(true);
    try {
      const updated = await confirmLinkedInIdentity(candidate.id, accept);
      onUpdated(updated);
      notifications.show(accept
        ? { title: t("linkedin_refetch_queued"), message: t("linkedin_refetch_queued_msg"), color: "blue" }
        : { title: t("linkedin_mismatch_removed"), message: t("linkedin_mismatch_removed_msg"), color: "blue" });
      onRefresh?.();
    } catch {
      notifications.show({ title: t("error"), message: t("error"), color: "red" });
    } finally {
      setRefetching(false);
    }
  }

  // Manual safety net: the user can verify the 404 in a browser and flag the link
  // invalid even when the scraper couldn't auto-detect it. Un-flagging happens
  // naturally via "Try anyway" (a successful fetch clears it) or "Edit URL".
  async function markLinkInvalid() {
    try {
      const updated = await updateCandidate(candidate.id, { linkedInUrlDead: true });
      onUpdated(updated);
      notifications.show({
        title: t("linkedin_marked_invalid"),
        message: t("linkedin_marked_invalid_msg"),
        color: "orange",
      });
    } catch {
      notifications.show({ title: t("error"), message: t("error"), color: "red" });
    }
  }

  function openEditUrlModal() {
    modals.open({
      title: t("linkedin_edit_url"),
      centered: true,
      children: (
        <EditLinkedInUrlModalBody
          initial={candidate.linkedInUrl ?? ""}
          candidate={candidate}
          onSave={async (url) => {
            // Pass the trimmed value verbatim: a corrected URL clears the dead flag
            // server-side and re-enriches when it changed; an empty value removes the
            // URL entirely (both honour the alert's "correct the URL or remove it").
            const updated = await updateCandidate(candidate.id, { linkedInUrl: url });
            onUpdated(updated);
            onRefresh?.();
          }}
        />
      ),
    });
  }

  function startEditCareer(idx) {
    setEditingIdx(idx);
    setDraft(idx === -1
      ? { role: "", companyName: "", location: "", from: "", to: "", description: "" }
      : { ...career[idx] });
  }

  function startEditEdu(idx) {
    setEditingEduIdx(idx);
    setEduDraft(idx === -1
      ? { description: "", year: "" }
      : { ...education[idx] });
  }

  async function saveCareer() {
    if (!draft.role?.trim() || !draft.from?.trim()) return;
    setSaving(true);
    try {
      const updated = [...career];
      if (editingIdx === -1) {
        updated.unshift(draft);
      } else {
        updated[editingIdx] = draft;
      }
      const result = await updateCandidate(candidate.id, { career: updated });
      onUpdated(result);
      setEditingIdx(null);
      setDraft(null);
    } catch {
      notifications.show({ title: t("error"), message: t("error"), color: "red" });
    } finally {
      setSaving(false);
    }
  }

  async function deleteCareer(idx) {
    setSaving(true);
    try {
      const updated = career.filter((_, i) => i !== idx);
      const result = await updateCandidate(candidate.id, { career: updated });
      onUpdated(result);
    } catch {
      notifications.show({ title: t("error"), message: t("error"), color: "red" });
    } finally {
      setSaving(false);
    }
  }

  async function saveEducation() {
    if (!eduDraft.description?.trim()) return;
    setSaving(true);
    try {
      const updated = [...education];
      if (editingEduIdx === -1) {
        updated.unshift(eduDraft);
      } else {
        updated[editingEduIdx] = eduDraft;
      }
      const result = await updateCandidate(candidate.id, { education: updated });
      onUpdated(result);
      setEditingEduIdx(null);
      setEduDraft(null);
    } catch {
      notifications.show({ title: t("error"), message: t("error"), color: "red" });
    } finally {
      setSaving(false);
    }
  }

  async function deleteEducation(idx) {
    setSaving(true);
    try {
      const updated = education.filter((_, i) => i !== idx);
      const result = await updateCandidate(candidate.id, { education: updated });
      onUpdated(result);
    } catch {
      notifications.show({ title: t("error"), message: t("error"), color: "red" });
    } finally {
      setSaving(false);
    }
  }

  const careerEntryForm = () => (
    <Card p="sm" withBorder mb="sm">
      <Stack gap="xs">
        <Grid>
          <Grid.Col span={{ base: 12, xs: 6 }}>
            <TextInput label={t("role")} size="xs" required value={draft.role}
              onChange={(e) => setDraft({ ...draft, role: e.target.value })} />
          </Grid.Col>
          <Grid.Col span={{ base: 12, xs: 6 }}>
            <CompanyAutocomplete label={t("company")} size="xs" value={draft.companyName ?? ""}
              onChange={({ name, id }) => setDraft({ ...draft, companyName: name, companyId: id })} />
          </Grid.Col>
        </Grid>
        <Grid>
          <Grid.Col span={{ base: 6, xs: 3 }}>
            <TextInput label={t("from")} size="xs" required placeholder="2020" value={draft.from}
              onChange={(e) => setDraft({ ...draft, from: e.target.value })} />
          </Grid.Col>
          <Grid.Col span={{ base: 6, xs: 3 }}>
            <TextInput label={t("to")} size="xs" placeholder={t("present")} value={draft.to ?? ""}
              onChange={(e) => setDraft({ ...draft, to: e.target.value })} />
          </Grid.Col>
          <Grid.Col span={{ base: 12, xs: 6 }}>
            <TextInput label={t("location")} size="xs" value={draft.location ?? ""}
              onChange={(e) => setDraft({ ...draft, location: e.target.value })} />
          </Grid.Col>
        </Grid>
        <Textarea label={t("description")} size="xs" autosize minRows={2} maxRows={6}
          value={draft.description ?? ""}
          onChange={(e) => setDraft({ ...draft, description: e.currentTarget.value })} />
        <Group gap="xs" justify="flex-end">
          <Button size="xs" variant="subtle" onClick={() => { setEditingIdx(null); setDraft(null); }}>{t("cancel")}</Button>
          <Button size="xs" loading={saving} disabled={!draft.role?.trim() || !draft.from?.trim()} onClick={saveCareer}>{t("save")}</Button>
        </Group>
      </Stack>
    </Card>
  );

  const educationEntryForm = () => (
    <Card p="sm" withBorder mb="sm">
      <Stack gap="xs">
        <Grid>
          <Grid.Col span={{ base: 12, xs: 8 }}>
            <TextInput label={t("description")} size="xs" required value={eduDraft.description}
              onChange={(e) => setEduDraft({ ...eduDraft, description: e.target.value })} />
          </Grid.Col>
          <Grid.Col span={{ base: 12, xs: 4 }}>
            <TextInput label={t("year")} size="xs" placeholder="2015" value={eduDraft.year ?? ""}
              onChange={(e) => setEduDraft({ ...eduDraft, year: e.target.value })} />
          </Grid.Col>
        </Grid>
        <Group gap="xs" justify="flex-end">
          <Button size="xs" variant="subtle" onClick={() => { setEditingEduIdx(null); setEduDraft(null); }}>{t("cancel")}</Button>
          <Button size="xs" loading={saving} disabled={!eduDraft.description?.trim()} onClick={saveEducation}>{t("save")}</Button>
        </Group>
      </Stack>
    </Card>
  );

  return (
    <Stack gap="md">
      {/* Summary text — full width on top */}
      <Card>
        <Group justify="space-between" mb="sm">
          <Title order={5}>
            <Group gap="xs"><IconUser size={16} /> {t("summary")}</Group>
          </Title>
          {!editingSummary && (
            <Group gap="xs">
              <Button size="xs" variant="light" leftSection={<IconRobot size={14} />}
                loading={generatingSummary} onClick={handleGenerateSummary}>
                {t("ai_summary")}
              </Button>
              <ActionIcon size="sm" variant="subtle" color="gray"
                onClick={() => { setEditingSummary(true); setSummaryDraft(candidate.summary ?? ""); }}>
                <IconPencil size={14} />
              </ActionIcon>
            </Group>
          )}
        </Group>
        {editingSummary ? (
          <Stack gap="xs">
            <Textarea
              autosize
              minRows={4}
              maxRows={20}
              value={summaryDraft}
              onChange={(e) => setSummaryDraft(e.currentTarget.value)}
            />
            <Group gap="xs" justify="flex-end">
              <Button size="xs" variant="subtle" onClick={() => setEditingSummary(false)}>{t("cancel")}</Button>
              <Button size="xs" loading={savingSummary} onClick={saveSummary}>{t("save")}</Button>
            </Group>
          </Stack>
        ) : candidate.summary ? (
          <Text size="sm" style={{ whiteSpace: "pre-wrap" }}
            dangerouslySetInnerHTML={{ __html: linkifySummary(candidate.summary) }} />
        ) : (
          <Text size="sm" c="dimmed">{t("no_summary")}</Text>
        )}
      </Card>

    <Grid>
      <Grid.Col span={{ base: 12, md: 8 }}>
        <Card>
          <Group justify="space-between" mb="md">
            <Title order={5}>{t("career_history")}</Title>
            {editingIdx === null && (
              <Group gap="xs">
                {candidate.linkedInUrl && !linkInvalid && (
                  <Tooltip label={t("linkedin_mark_invalid")} withArrow>
                    <ActionIcon size="lg" variant="subtle" color="gray"
                      aria-label={t("linkedin_mark_invalid")}
                      disabled={enrichInProgress}
                      onClick={markLinkInvalid}>
                      <IconUnlink size={16} />
                    </ActionIcon>
                  </Tooltip>
                )}
                {candidate.linkedInUrl && !linkInvalid && (
                  <Button size="xs"
                    variant={enrichInProgress ? "light" : "subtle"}
                    color={enrichInProgress ? "blue" : undefined}
                    leftSection={enrichInProgress
                      ? <Loader size={14} />
                      : <IconBrandLinkedin size={14} />}
                    loading={refetching}
                    disabled={enrichInProgress}
                    onClick={openRefetchModal}>
                    {enrichInProgress
                      ? enrichmentStatusLabel(enrichmentStatus, t)
                      : t("refetch_linkedin")}
                  </Button>
                )}
                <Button size="xs" variant="light" leftSection={<IconPlus size={14} />}
                  onClick={() => startEditCareer(-1)}>
                  {t("add")}
                </Button>
              </Group>
            )}
          </Group>

          {linkInvalid && (
            <Alert color="red" icon={<IconAlertTriangle size={16} />} mb="md"
              title={t("linkedin_invalid_title")}>
              <Text size="sm">{t("linkedin_invalid_body")}</Text>
              <Group gap="xs" mt="sm">
                <Button size="xs" variant="white" color="red"
                  leftSection={<IconBrandLinkedin size={14} />} onClick={openEditUrlModal}>
                  {t("linkedin_edit_url")}
                </Button>
                <Button size="xs" variant="subtle" color="red"
                  loading={refetching} disabled={enrichInProgress} onClick={tryRefetchAnyway}>
                  {t("linkedin_try_anyway")}
                </Button>
              </Group>
            </Alert>
          )}

          {candidate.linkedInNameMismatch && candidate.linkedInUrl && (
            <Alert color="yellow" icon={<IconAlertTriangle size={16} />} mb="md"
              title={t("linkedin_mismatch_title")}>
              <Text size="sm">
                {t("linkedin_mismatch_body", {
                  profileName: candidate.linkedInNameMismatch,
                  candidateName: candidate.name,
                })}
              </Text>
              <Group gap="xs" mt="sm">
                <Button size="xs" variant="white" color="yellow"
                  leftSection={<IconCheck size={14} />}
                  loading={refetching} disabled={enrichInProgress}
                  onClick={() => resolveIdentityMismatch(true)}>
                  {t("linkedin_mismatch_accept")}
                </Button>
                <Button size="xs" variant="subtle" color="yellow"
                  leftSection={<IconBrandLinkedin size={14} />} onClick={openEditUrlModal}>
                  {t("linkedin_edit_url")}
                </Button>
                <Button size="xs" variant="subtle" color="red"
                  leftSection={<IconUnlink size={14} />}
                  loading={refetching} disabled={enrichInProgress}
                  onClick={() => resolveIdentityMismatch(false)}>
                  {t("linkedin_mismatch_reject")}
                </Button>
              </Group>
            </Alert>
          )}

          {editingIdx === -1 && careerEntryForm()}

          {career.length === 0 && editingIdx !== -1 ? (
            enrichmentStatus === "failed" ? (
              <Text size="sm" c="red">{t("enrichment_failed")}</Text>
            ) : (
              <Text size="sm" c="dimmed">{t("no_career")}</Text>
            )
          ) : (
            <Stack gap={0}>
              {career.map((pos, i) => (
                editingIdx === i ? (
                  careerEntryForm()
                ) : (
                  <Group key={i} gap="xs" align="flex-start" wrap="nowrap" className="hover-actions-parent"
                    py="xs" style={{ borderBottom: i < career.length - 1 ? "1px solid var(--mantine-color-default-border)" : undefined }}>
                    {pos.companyName && (
                      <CompanyLogo companyId={pos.companyId} companyName={pos.companyName}
                        size={28} color="gray" style={{ flexShrink: 0, marginTop: 2 }} />
                    )}
                    <div style={{ minWidth: 0, flex: 1 }}>
                      {pos.companyName && (pos.companyId ? (
                        <Anchor component="button" type="button" size="sm" fw={500}
                          onClick={() => setLocation(`/companies/${pos.companyId}`)}
                          style={{ display: "block", textAlign: "left" }}>
                          {pos.companyName}
                        </Anchor>
                      ) : (
                        <Text size="sm" fw={500}>{pos.companyName}</Text>
                      ))}
                      <Group gap="xs">
                        <Text size="sm" c="dimmed">{careerDate(pos.from)} – {pos.to && !["present", "heute"].includes(pos.to.trim().toLowerCase()) ? careerDate(pos.to) : t("present")}</Text>
                        <Text size="sm" c={pos.companyName ? "dimmed" : undefined} fw={pos.companyName ? undefined : 500}>{pos.role}</Text>
                      </Group>
                      {pos.location && <Text size="xs" c="dimmed">{pos.location}</Text>}
                      {pos.description && <Text size="xs" c="dimmed" mt={2} style={{ whiteSpace: "pre-wrap" }}>{pos.description}</Text>}
                    </div>
                    <Group gap={4} wrap="nowrap" align="center" className="hover-actions">
                      {pos.id && entrySource("career", pos.id) && (
                        <EntryHistory candidateId={candidate.id} field="career" entryId={pos.id}
                          sourceKind={entrySource("career", pos.id)} onRestored={onUpdated} />
                      )}
                      <ActionIcon size="xs" variant="subtle" color="gray" onClick={() => startEditCareer(i)}>
                        <IconPencil size={12} />
                      </ActionIcon>
                      <ActionIcon size="xs" variant="subtle" color="red" loading={saving} onClick={() => deleteCareer(i)}>
                        <IconTrash size={12} />
                      </ActionIcon>
                    </Group>
                  </Group>
                )
              ))}
            </Stack>
          )}
        </Card>
      </Grid.Col>

      <Grid.Col span={{ base: 12, md: 4 }}>
        <Card>
          <Group justify="space-between" mb="md">
            <Title order={5}>{t("education")}</Title>
            {editingEduIdx === null && (
              <Button size="xs" variant="light" leftSection={<IconPlus size={14} />}
                onClick={() => startEditEdu(-1)}>
                {t("add")}
              </Button>
            )}
          </Group>

          {editingEduIdx === -1 && educationEntryForm()}

          {education.length === 0 && editingEduIdx !== -1 ? (
            <Text size="sm" c="dimmed">{t("no_education")}</Text>
          ) : (
            <Stack gap={0}>
              {education.map((edu, i) => (
                editingEduIdx === i ? (
                  educationEntryForm()
                ) : (
                  <Group key={i} gap="xs" align="flex-start" wrap="nowrap" className="hover-actions-parent"
                    py="xs" style={{ borderBottom: i < education.length - 1 ? "1px solid var(--mantine-color-default-border)" : undefined }}>
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <Text size="sm" fw={500}>{edu.description}</Text>
                      {edu.year && <Text size="xs" c="dimmed">{edu.year}</Text>}
                    </div>
                    <Group gap={4} wrap="nowrap" align="center" className="hover-actions">
                      {edu.id && entrySource("education", edu.id) && (
                        <EntryHistory candidateId={candidate.id} field="education" entryId={edu.id}
                          sourceKind={entrySource("education", edu.id)} onRestored={onUpdated} />
                      )}
                      <ActionIcon size="xs" variant="subtle" color="gray" onClick={() => startEditEdu(i)}>
                        <IconPencil size={12} />
                      </ActionIcon>
                      <ActionIcon size="xs" variant="subtle" color="red" loading={saving} onClick={() => deleteEducation(i)}>
                        <IconTrash size={12} />
                      </ActionIcon>
                    </Group>
                  </Group>
                )
              ))}
            </Stack>
          )}
        </Card>
        <ColleaguesCard candidate={candidate} />

        {/* Skills */}
        <Card mt="md">
          <Title order={5} mb="sm">
            <Group gap="xs"><IconNotes size={16} /> {t("skills")}</Group>
          </Title>
          <Stack gap="xs">
            <Group gap={4}>
              {visibleSkills.map((s) => (
                <Badge key={s} size="xs" variant="light" color="teal"
                  rightSection={
                    <CloseButton size={12} variant="transparent" onClick={() => removeSkill(s)} />
                  }
                >
                  {s}
                </Badge>
              ))}
              {!showAllSkills && hiddenCount > 0 && (
                <Badge size="xs" variant="light" color="gray" style={{ cursor: "pointer" }}
                  onClick={() => setShowAllSkills(true)}>
                  +{hiddenCount}
                </Badge>
              )}
              {showAllSkills && hiddenCount > 0 && (
                <Badge size="xs" variant="light" color="gray" style={{ cursor: "pointer" }}
                  onClick={() => setShowAllSkills(false)}>
                  {t("show_less")}
                </Badge>
              )}
              {skills.length === 0 && <Text size="sm" c="dimmed">—</Text>}
            </Group>
            <Group gap="xs">
              <TextInput
                placeholder={t("add_skill")}
                size="xs"
                value={skillInput}
                onChange={(e) => setSkillInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") addSkill(); }}
                style={{ flex: 1 }}
              />
              <Button size="xs" variant="light" leftSection={<IconPlus size={12} />}
                disabled={!skillInput.trim()} loading={addingSkill} onClick={addSkill}>
                {t("add")}
              </Button>
            </Group>
          </Stack>
        </Card>

        {/* Languages */}
        <Card mt="md">
          <LanguagesEditor candidate={candidate} onUpdated={onUpdated} />
        </Card>

        {/* Compensation */}
        <CompensationEditor candidate={candidate} onUpdated={onUpdated} />

        {/* Metadata */}
        <Card mt="md">
          <Title order={5} mb="sm">
            <Group gap="xs"><IconNotes size={16} /> {t("metadata")}</Group>
          </Title>
          <Stack gap="xs">
            <Group gap="xs" align="center">
              <Text size="sm" fw={500}>{t("enrichment")}:</Text>
              {!enrichInProgress && (
                <Badge size="xs" color={enrichmentStatusColor(enrichmentStatus)}>
                  {enrichmentStatusLabel(enrichmentStatus, t)}
                </Badge>
              )}
              <InfoHint i18nKey="enrichment_what_help" multiline width={320} />
              {!enrichInProgress && (enrichmentStatus === "failed" || !enrichmentStatus || enrichmentStatus === "none") && (
                <Anchor component="button" type="button" size="xs" onClick={reEnrichFromSidebar}>
                  {t("re_enrich")}
                </Anchor>
              )}
            </Group>
            {enrichInProgress && <EnrichmentSteps status={enrichmentStatus} t={t} />}
            {enrichmentStatus === "failed" && (
              <Text size="xs" c="dimmed">{t("enrichment_failed_help")}</Text>
            )}
            {/* Per-source freshness so it's clear WHEN LinkedIn/web data was last pulled, and why
                web search hasn't run (no disambiguating title/company/location). */}
            <Group gap="xs">
              <Text size="sm" fw={500}>{t("enrich_linkedin")}:</Text>
              <Text size="sm" c="dimmed">
                {!candidate.linkedInUrl ? t("enrich_li_nourl")
                  : candidate.linkedInUrlDead ? t("enrich_li_invalid")
                  : candidate.linkedInNameMismatch ? t("enrich_li_mismatch")
                  : candidate.lastLinkedInFetch ? t("enrich_li_updated", { date: formatDate(candidate.lastLinkedInFetch) })
                  : t("enrich_li_notyet")}
              </Text>
            </Group>
            {(() => {
              const ws = sources.filter((s) => s.sourceType === "webSearch");
              // lastWebSearchAt covers completed-but-empty searches (they create no source);
              // the newest of stamp/source is the honest "last searched" date.
              const latestSource = ws.length > 0
                ? ws.reduce((a, b) => (new Date(a.retrievedAt) > new Date(b.retrievedAt) ? a : b)).retrievedAt
                : null;
              const lastSearched = [latestSource, candidate.lastWebSearchAt]
                .filter(Boolean)
                .sort((a, b) => new Date(b) - new Date(a))[0];
              if (lastSearched) {
                return (
                  <Group gap="xs">
                    <Text size="sm" fw={500}>{t("enrich_web")}:</Text>
                    <Text size="sm" c="dimmed">{t("enrich_web_searched", { date: formatDate(lastSearched) })}</Text>
                  </Group>
                );
              }
              const hasContext = candidate.title || candidate.career?.[0]?.companyName || candidate.location;
              if (hasContext) {
                return (
                  <Group gap="xs">
                    <Text size="sm" fw={500}>{t("enrich_web")}:</Text>
                    <Text size="sm" c="dimmed">{t("enrich_web_notyet")}</Text>
                  </Group>
                );
              }
              // Not enough identity to disambiguate — make it an actionable warning, not a dead end.
              return (
                <Group gap="xs" align="center">
                  <Text size="sm" fw={500}>{t("enrich_web")}:</Text>
                  <Text size="sm" c="orange">{t("enrich_web_nocontext")}</Text>
                  <InfoHint i18nKey="enrich_web_nocontext_help" multiline width={300} />
                </Group>
              );
            })()}
            {isAdmin && candidate.enrichmentError && (
              <Group gap="xs" align="flex-start">
                <Text size="sm" fw={500}>{t("enrichment_error")}:</Text>
                <Text size="xs" c="red" style={{ flex: 1, wordBreak: "break-word" }}>
                  {candidate.enrichmentError}
                </Text>
              </Group>
            )}
            <Group gap="xs">
              <Text size="sm" fw={500}>{t("sources")}:</Text>
              <Text size="sm">{t("documents_count", { count: sources.length })}</Text>
            </Group>
            <ConsentSection candidate={candidate} onChanged={onRefresh} />
            <Group gap="xs">
              <Text size="sm" fw={500}>{t("timeline_entries")}:</Text>
              <Text size="sm">{activities.length}</Text>
            </Group>
            {activities.length > 0 && (
              <>
                <Group gap="xs">
                  <Text size="sm" fw={500}>{t("last_interaction")}:</Text>
                  <Text size="sm">{formatDate(activities[0].createdAt)}</Text>
                </Group>
                <Group gap="xs">
                  <Text size="sm" fw={500}>{t("first_contact")}:</Text>
                  <Text size="sm">{formatDate(activities[activities.length - 1].createdAt)}</Text>
                </Group>
              </>
            )}
          </Stack>
        </Card>

        {/* Field sources — per-field provenance ([CV]/[LinkedIn]/[Manual]/…), a read of the
            FieldChange log. Only fields with a known source render. */}
        {fieldSources.fields.some((fs) => PROVENANCE_SOURCES.includes(fs.sourceKind)) && (
          <Card mt="md">
            <Title order={5} mb="sm">
              <Group gap="xs">
                <IconHistory size={16} /> {t("field_sources_title")}
                <InfoHint i18nKey="field_sources_help" multiline width={320} />
              </Group>
            </Title>
            <Stack gap={6}>
              {fieldSources.fields
                .filter((fs) => PROVENANCE_SOURCES.includes(fs.sourceKind))
                .map((fs) => (
                  <Group key={fs.fieldName} gap="xs" justify="space-between" wrap="nowrap">
                    <Text size="sm">{fieldLabel(fs.fieldName, t)}</Text>
                    <ProvenanceBadge sourceKind={fs.sourceKind} />
                  </Group>
                ))}
            </Stack>
          </Card>
        )}
      </Grid.Col>
    </Grid>
    </Stack>
  );
}

// Source kinds that render a provenance badge (must mirror ProvenanceBadge's SOURCE_CONFIG).
const PROVENANCE_SOURCES = ["manual", "cv", "linkedin", "crmNotes", "import"];

const sourceTypeTKeys = {
  cvUpload: "source_file", linkedIn: "source_linkedin", webSearch: "source_web", manualEntry: "source_manual",
  vcfImport: "source_vcf", zipFolderImport: "source_zip", meetingTranscript: "source_transcript", publicData: "source_public",
};

// File uploads (cvUpload) are every dropped PDF, including NDAs — the SourceType badge used
// to label them all "Lebenslauf". Channel badges stay for LinkedIn/transcript/web; the
// content-kind badge (DocumentKind) is what a classified file actually is.
const sourceTypeBadgeHidden = new Set(["cvUpload"]);

const docKindColors = {
  cv: "blue", compensation: "yellow", consentForm: "teal", other: "gray",
};

// Person-link: who introduced or sourced this contact. Drives the "Source" computed role on the
// linked person. Lives in the header as read-only chips (this is low-frequency provenance, not a
// primary action) with a small popover to add/remove — it used to be an always-open edit block at
// the top of the timeline, which over-weighted it and pushed the note box down.
function SourcedByControl({ candidate, onUpdated }) {
  const { t } = useTranslation("Veft");
  const [, setLocation] = useLocation();
  const nameFormat = useStore((s) => s.nameFormat);
  const ids = candidate.sourcedByCandidateIds ?? [];
  const [resolved, setResolved] = useState({});
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [saving, setSaving] = useState(false);
  const [opened, setOpened] = useState(false);

  // Resolve ids to {name, title} for display.
  useEffect(() => {
    let cancelled = false;
    const missing = ids.filter((id) => !(id in resolved));
    if (missing.length === 0) return;
    Promise.all(missing.map((id) => getCandidate(id).catch(() => null))).then((cands) => {
      if (cancelled) return;
      setResolved((prev) => {
        const next = { ...prev };
        cands.forEach((c, i) => { if (c) next[missing[i]] = c; });
        return next;
      });
    });
    return () => { cancelled = true; };
  }, [ids.join(",")]);

  // Debounced name search, excluding self and already-added sources.
  useEffect(() => {
    if (!query.trim()) { setResults([]); return; }
    const handle = setTimeout(async () => {
      try {
        const found = await searchCandidates(query.trim());
        const filtered = (found ?? []).filter((c) =>
          c.id !== candidate.id && !ids.includes(c.id));
        setResults(filtered.slice(0, 8));
      } catch {
        setResults([]);
      }
    }, 200);
    return () => clearTimeout(handle);
  }, [query, ids.join(","), candidate.id]);

  async function persist(nextIds) {
    setSaving(true);
    try {
      const updated = await setCandidateSourcedBy(candidate.id, nextIds);
      onUpdated(updated);
    } catch {
      notifications.show({ color: "red", title: t("error"), message: t("error") });
    } finally {
      setSaving(false);
    }
  }

  function addSource(c) {
    if (!c || ids.includes(c.id)) return;
    setResolved((prev) => ({ ...prev, [c.id]: c }));
    setQuery("");
    setResults([]);
    persist([...ids, c.id]);
  }

  function removeSource(id) {
    persist(ids.filter((x) => x !== id));
  }

  const popover = (
    <Popover opened={opened} onChange={setOpened} position="bottom-start" width={300} withinPortal trapFocus
      onClose={() => { setQuery(""); setResults([]); }}>
      <Popover.Target>
        {ids.length === 0 ? (
          <Button size="compact-xs" variant="subtle" color="gray" leftSection={<IconPlus size={12} />}
            style={{ fontWeight: 400 }} onClick={() => setOpened((o) => !o)}>
            {t("sourced_by")}
          </Button>
        ) : (
          <Tooltip label={t("sourced_by")}>
            <ActionIcon size="xs" variant="subtle" color="gray" onClick={() => setOpened((o) => !o)}
              aria-label={t("sourced_by")}>
              <IconPlus size={12} />
            </ActionIcon>
          </Tooltip>
        )}
      </Popover.Target>
      <Popover.Dropdown>
        <Text size="xs" c="dimmed" mb={6}>{t("sourced_by_help")}</Text>
        <TextInput
          size="xs"
          autoFocus
          placeholder={t("search_candidates")}
          leftSection={<IconSearch size={12} />}
          value={query}
          onChange={(e) => setQuery(e.currentTarget.value)}
          disabled={saving}
        />
        {results.length > 0 && (
          <Stack gap={2} mt={6} style={{ maxHeight: 180, overflow: "auto" }}>
            {results.map((c) => (
              <Group key={c.id} gap="xs" p={4} style={{ cursor: "pointer", borderRadius: 4 }} onClick={() => addSource(c)}
                onMouseEnter={(e) => e.currentTarget.style.background = "var(--mantine-color-gray-light)"}
                onMouseLeave={(e) => e.currentTarget.style.background = "transparent"}>
                <Text size="xs" fw={500}>{formatCandidateName(c, nameFormat)}</Text>
                {c.title && <Text size="xs" c="dimmed" truncate>— {c.title}</Text>}
              </Group>
            ))}
          </Stack>
        )}
      </Popover.Dropdown>
    </Popover>
  );

  // Empty → just the quiet "+ Quelle" affordance. Populated → label + removable chips + add button.
  if (ids.length === 0) return popover;
  return (
    <Group gap={6} align="center" wrap="wrap">
      <Text size="xs" c="dimmed">{t("sourced_by")}:</Text>
      {ids.map((id) => {
        const c = resolved[id];
        const label = c ? (formatCandidateName(c, nameFormat) || id) : "…";
        return (
          <Badge
            key={id}
            size="sm" variant="light" color="orange"
            style={{ textTransform: "none", cursor: c ? "pointer" : "default" }}
            onClick={() => { if (c) setLocation(`/candidates/${id}`); }}
            rightSection={
              <ActionIcon size="xs" variant="transparent" color="orange"
                onClick={(e) => { e.stopPropagation(); removeSource(id); }} aria-label={t("remove")}>
                <IconX size={10} />
              </ActionIcon>
            }
          >
            {label}
          </Badge>
        );
      })}
      {popover}
    </Group>
  );
}

function TagsEditor({ candidate, tagDefs, onUpdated }) {
  const { t } = useTranslation("Veft");
  const [saving, setSaving] = useState(false);
  const currentTags = candidate.tags ?? [];
  const suggestions = tagDefs.map((td) => td.name).filter((n) => !currentTags.includes(n));

  async function handleChange(next) {
    const dedup = Array.from(new Set(next.map((s) => s.trim()).filter(Boolean)));
    setSaving(true);
    try {
      const updated = await updateCandidate(candidate.id, { tags: dedup });
      onUpdated(updated);
    } catch {
      notifications.show({ title: t("error"), message: t("error"), color: "red" });
    } finally {
      setSaving(false);
    }
  }

  return (
    <TagsInput
      label={t("tags")}
      value={currentTags}
      onChange={handleChange}
      data={suggestions}
      placeholder={t("add_tag")}
      disabled={saving}
      clearable
      acceptValueOnBlur
    />
  );
}

function TranscriptAnalysisPanel({ candidateId, source, onSourcesChanged }) {
  const { t } = useTranslation("Veft");
  const [analysis, setAnalysis] = useState(source.transcriptAnalysis);
  const [loading, setLoading] = useState(false);
  const [confirming, setConfirming] = useState(null);

  useEffect(() => {
    if (source.transcriptAnalysis) {
      setAnalysis(source.transcriptAnalysis);
      return;
    }
    // Poll for analysis results if source is a transcript without analysis yet
    if (source.sourceType !== "meetingTranscript") return;
    let cancelled = false;
    const poll = async () => {
      setLoading(true);
      try {
        const result = await getTranscriptAnalysis(candidateId, source.id);
        if (!cancelled) setAnalysis(result);
      } catch {
        // Not ready yet
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    poll();
    const interval = setInterval(poll, 5000);
    return () => { cancelled = true; clearInterval(interval); };
  }, [source.id]);

  async function handleConfirmProject(projectId) {
    setConfirming(projectId);
    try {
      await confirmProjectLink(candidateId, source.id, projectId);
      notifications.show({ title: t("added"), message: t("added_to_project_msg"), color: "green" });
      onSourcesChanged();
    } catch {
      notifications.show({ title: t("error"), message: t("error"), color: "red" });
    } finally {
      setConfirming(null);
    }
  }

  if (loading && !analysis) {
    return (
      <Card mt="sm" p="sm" bg="orange.0">
        <Group gap="xs"><Loader size="xs" /> <Text size="xs" c="dimmed">{t("analysis_in_progress")}</Text></Group>
      </Card>
    );
  }
  if (!analysis) return null;

  return (
    <Card mt="sm" p="sm" bg="orange.0">
      <Stack gap="xs">
        <Text size="xs" fw={600}>{t("transcript_analysis")}</Text>
        <Text size="xs" style={{ whiteSpace: "pre-wrap" }}>{analysis.summary}</Text>

        {analysis.candidateInsights?.length > 0 && (
          <div>
            <Text size="xs" fw={600} mt="xs">{t("insights")}</Text>
            {analysis.candidateInsights.map((insight, i) => (
              <Text key={i} size="xs" pl="sm">{"\u2022"} {insight}</Text>
            ))}
          </div>
        )}

        {analysis.projectInsights?.length > 0 && (
          <div>
            <Text size="xs" fw={600} mt="xs">{t("projects")}</Text>
            {analysis.projectInsights.map((pi, i) => (
              <Card key={i} p="xs" mt={4} withBorder>
                <Group justify="space-between" align="flex-start">
                  <div>
                    <Group gap="xs">
                      <Text size="xs" fw={500}>{pi.projectName}</Text>
                      {pi.interestLevel && (
                        <Badge size="xs" variant="light"
                          color={pi.interestLevel === "high" ? "green" : pi.interestLevel === "low" ? "red" : "yellow"}>
                          {pi.interestLevel}
                        </Badge>
                      )}
                      {pi.resolvedProjectId
                        ? <Badge size="xs" color="green" variant="light">{t("linked")}</Badge>
                        : <Badge size="xs" color="gray" variant="light">{t("unresolved")}</Badge>
                      }
                    </Group>
                    <Text size="xs" mt={2}>{pi.summary}</Text>
                  </div>
                  {pi.resolvedProjectId && !analysis._confirmedProjects?.includes(pi.resolvedProjectId) && (
                    <Button size="xs" variant="light" loading={confirming === pi.resolvedProjectId}
                      onClick={() => handleConfirmProject(pi.resolvedProjectId)}>
                      {t("add_to_project")}
                    </Button>
                  )}
                </Group>
              </Card>
            ))}
          </div>
        )}

        {analysis.personMentions?.length > 0 && (
          <div>
            <Text size="xs" fw={600} mt="xs">{t("people_mentioned")}</Text>
            {analysis.personMentions.map((pm, i) => (
              <Group key={i} gap="xs" mt={2}>
                <Text size="xs" fw={500}>{pm.name}</Text>
                {pm.relationship && <Badge size="xs" variant="light">{pm.relationship}</Badge>}
                {pm.resolvedCandidateId && <Badge size="xs" color="green" variant="light">Known</Badge>}
                <Text size="xs" c="dimmed">{pm.context}</Text>
              </Group>
            ))}
          </div>
        )}

        {analysis.compensation && (
          <div>
            <Text size="xs" fw={600} mt="xs">{t("compensation")}</Text>
            <Text size="xs">
              {[
                analysis.compensation.baseSalary && `${t("comp_base")} ${analysis.compensation.baseSalary.toLocaleString()}`,
                analysis.compensation.totalCompensation && `${t("comp_total")} ${analysis.compensation.totalCompensation.toLocaleString()}`,
                analysis.compensation.currency,
              ].filter(Boolean).join(" / ")}
              {analysis.compensation.note && ` — ${analysis.compensation.note}`}
            </Text>
          </div>
        )}
      </Stack>
    </Card>
  );
}

function DocumentsTab({ candidate, sources, onSourcesChanged }) {
  const { t, i18n } = useTranslation("Veft");
  const costBasis = useEnrichmentCostBasis();
  const candidateId = candidate.id;
  const enrichmentStatus = candidate.enrichmentStatus;
  const enrichInProgress = enrichmentStatus &&
    enrichmentStatus !== "none" &&
    enrichmentStatus !== "complete" &&
    enrichmentStatus !== "failed";
  const [uploading, setUploading] = useState(false);
  const [enriching, setEnriching] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [showTranscriptForm, setShowTranscriptForm] = useState(false);
  const [transcriptText, setTranscriptText] = useState("");
  const [transcriptType, setTranscriptType] = useState("interview");
  const [submittingTranscript, setSubmittingTranscript] = useState(false);

  const transcriptTypeOptions = [
    { value: "interview", label: t("transcript_interview") },
    { value: "referenceCall", label: t("transcript_reference") },
    { value: "press", label: t("transcript_press") },
    { value: "informal", label: t("transcript_informal") },
  ];

  async function uploadFiles(files) {
    if (!files?.length) return;
    setUploading(true);
    try {
      for (const file of files) {
        await uploadCandidateSource(candidateId, file);
      }
      notifications.show({ title: t("uploaded"), message: t("upload_success_msg"), color: "green" });
      onSourcesChanged();
    } catch {
      notifications.show({ title: t("error"), message: t("upload_failed"), color: "red" });
    } finally {
      setUploading(false);
    }
  }

  function confirmDeleteSource(source) {
    const extracted = !!(source.extractedText || source.rawContent);
    modals.openConfirmModal({
      title: t("delete_source_title"),
      centered: true,
      children: (
        <Text size="sm">
          {extracted ? t("delete_source_confirm_extracted") : t("delete_source_confirm")}
        </Text>
      ),
      labels: { confirm: t("delete"), cancel: t("cancel") },
      confirmProps: { color: "red" },
      onConfirm: async () => {
        try {
          await deleteCandidateSource(candidateId, source.id);
          notifications.show({ title: t("deleted_notification"), message: t("document_removed"), color: "green" });
          onSourcesChanged();
        } catch {
          notifications.show({ color: "red", title: t("error"), message: t("delete_document_failed") });
        }
      },
    });
  }

  function openEnrichModal() {
    let forceOverwriteCareerEducation = false;
    modals.openConfirmModal({
      title: t("reenrich_candidate"),
      centered: true,
      children: (
        <Stack gap="sm">
          <Text size="sm">{t("reenrich_explanation")}</Text>
          {costBasis && (
            <Text size="sm" c="dimmed">
              {t("reenrich_cost", {
                credits: costBasis.fullEnrichmentEstimate,
                price: creditPrice(costBasis, costBasis.fullEnrichmentEstimate, i18n.language),
              })}
            </Text>
          )}
          <Checkbox
            label={t("overwrite_career_education")}
            description={t("overwrite_career_education_hint")}
            defaultChecked={false}
            onChange={(e) => { forceOverwriteCareerEducation = e.currentTarget.checked; }}
          />
        </Stack>
      ),
      labels: { confirm: t("re_enrich"), cancel: t("cancel") },
      onConfirm: async () => {
        setEnriching(true);
        try {
          await enrichCandidate(candidateId, { forceOverwriteCareerEducation });
          notifications.show({ title: t("enrichment_queued"), message: t("enrichment_queued_msg"), color: "blue" });
          onSourcesChanged?.();
        } catch {
          notifications.show({ title: t("error"), message: t("error"), color: "red" });
        } finally {
          setEnriching(false);
        }
      },
    });
  }

  async function handleSubmitTranscript() {
    if (!transcriptText.trim()) return;
    setSubmittingTranscript(true);
    try {
      await submitTranscript(candidateId, {
        text: transcriptText.trim(),
        transcriptType,
      });
      notifications.show({
        title: t("transcript_submitted"),
        message: t("transcript_submitted_msg"),
        color: "green",
      });
      setTranscriptText("");
      setShowTranscriptForm(false);
      onSourcesChanged();
    } catch {
      notifications.show({ title: t("error"), message: t("error"), color: "red" });
    } finally {
      setSubmittingTranscript(false);
    }
  }

  return (
    <Stack gap="md">
      <Group justify="space-between">
        <Title order={5}>{t("source_documents")}</Title>
        <Group gap="xs">
          <Button
            variant="light" size="xs" color="orange"
            leftSection={<IconNotes size={14} />}
            onClick={() => setShowTranscriptForm(!showTranscriptForm)}
          >
            {t("add_transcript")}
          </Button>
          <Button
            variant={enrichInProgress ? "light" : "subtle"}
            color={enrichInProgress ? "blue" : undefined}
            size="xs"
            leftSection={enrichInProgress ? <Loader size={14} /> : <IconRefresh size={14} />}
            loading={enriching}
            disabled={enrichInProgress}
            onClick={openEnrichModal}
          >
            {enrichInProgress
              ? enrichmentStatusLabel(enrichmentStatus, t)
              : t("re_enrich")}
          </Button>
        </Group>
      </Group>
      <Text size="xs" c="dimmed">{t("documents_reenrich_hint")}</Text>
      <Dropzone
        onDrop={uploadFiles}
        loading={uploading}
        multiple
        accept={{
          "application/pdf": [".pdf"],
          "application/vnd.openxmlformats-officedocument.wordprocessingml.document": [".docx"],
          "application/msword": [".doc"],
          "application/rtf": [".rtf"],
        }}
        p="md"
      >
        <Group justify="center" gap="md" style={{ pointerEvents: "none" }}>
          <Dropzone.Accept>
            <IconUpload size={28} stroke={1.5} color="var(--mantine-color-blue-6)" />
          </Dropzone.Accept>
          <Dropzone.Reject>
            <IconX size={28} stroke={1.5} color="var(--mantine-color-red-6)" />
          </Dropzone.Reject>
          <Dropzone.Idle>
            <IconUpload size={28} stroke={1.5} color="var(--mantine-color-dimmed)" />
          </Dropzone.Idle>
          <div>
            <Text size="sm" fw={500}>{t("dropzone_title")}</Text>
            <Text size="xs" c="dimmed">{t("contact_dropzone_hint")}</Text>
          </div>
        </Group>
      </Dropzone>

      {showTranscriptForm && (
        <Card p="sm" withBorder>
          <Stack gap="xs">
            <Group gap="xs">
              <Select size="xs" value={transcriptType} onChange={setTranscriptType}
                data={transcriptTypeOptions} style={{ width: 160 }} />
            </Group>
            <Textarea
              placeholder={t("paste_transcript")}
              autosize minRows={6} maxRows={20}
              value={transcriptText}
              onChange={(e) => setTranscriptText(e.currentTarget.value)}
            />
            <Group justify="flex-end" gap="xs">
              <Button size="xs" variant="subtle" color="gray"
                onClick={() => { setShowTranscriptForm(false); setTranscriptText(""); }}>
                {t("cancel")}
              </Button>
              <Button size="xs" loading={submittingTranscript}
                disabled={!transcriptText.trim()} onClick={handleSubmitTranscript}>
                {t("submit_analyze")}
              </Button>
            </Group>
          </Stack>
        </Card>
      )}

      {sources.length === 0 ? (
        <Text size="sm" c="dimmed">{t("no_documents")}</Text>
      ) : (
        <Stack gap="sm">
          {sources.map((source) => {
            const fileName = source.metadata?.OriginalFileName ?? source.metadata?.originalFileName;
            const hasFile = !!source.fileBlobId;
            const hasExtracted = !!source.extractedText;
            const isExpanded = expandedId === source.id;
            const isTranscript = source.sourceType === "meetingTranscript";

            return (
              <Card key={source.id} p="sm" withBorder>
                <Group justify="space-between" align="flex-start">
                  <Group gap="sm">
                    <ThemeIcon
                      variant="light" size="lg"
                      color={isTranscript ? "orange" : hasExtracted ? "green" : hasFile ? "yellow" : "gray"}
                    >
                      {isTranscript ? <IconNotes size={18} /> : <IconFileText size={18} />}
                    </ThemeIcon>
                    <div>
                      <Group gap="xs">
                        <Text size="sm" fw={500}>
                          {fileName || t(sourceTypeTKeys[source.sourceType] || "source_web")}
                        </Text>
                        {!sourceTypeBadgeHidden.has(source.sourceType) && (
                          <Badge size="xs" variant="light" color={isTranscript ? "orange" : undefined}>
                            {t(sourceTypeTKeys[source.sourceType] || "source_web")}
                          </Badge>
                        )}
                        {source.documentKind && (
                          <Badge size="xs" variant="dot"
                            color={docKindColors[source.documentKind] || "gray"}>
                            {t(`doc_kind_${source.documentKind}`, { defaultValue: source.documentKind })}
                          </Badge>
                        )}
                        {isTranscript && source.metadata?.TranscriptType && (
                          <Badge size="xs" variant="outline">
                            {transcriptTypeOptions.find((tp) => tp.value === source.metadata.TranscriptType)?.label
                              || source.metadata.TranscriptType}
                          </Badge>
                        )}
                        {isTranscript && (
                          source.transcriptAnalysis
                            ? <Badge size="xs" color="green" variant="light" leftSection={<IconCheck size={10} />}>{t("analyzed")}</Badge>
                            : <Badge size="xs" color="yellow" variant="light" leftSection={<IconClock size={10} />}>{t("analyzing")}</Badge>
                        )}
                        {!isTranscript && hasFile && (
                          hasExtracted
                            ? <Badge size="xs" color="green" variant="light" leftSection={<IconCheck size={10} />}>{t("processed")}</Badge>
                            : <Badge size="xs" color="yellow" variant="light" leftSection={<IconClock size={10} />}>{t("pending")}</Badge>
                        )}
                      </Group>
                      <Text size="xs" c="dimmed">{formatDate(source.retrievedAt)}</Text>
                    </div>
                  </Group>
                  <Group gap="xs">
                    {(hasExtracted || source.rawContent) && (
                      <Button
                        variant="subtle" size="xs"
                        onClick={() => setExpandedId(isExpanded ? null : source.id)}
                      >
                        {isExpanded ? t("hide") : t("preview")}
                      </Button>
                    )}
                    {hasFile && (
                      <Button
                        variant="light" size="xs"
                        leftSection={<IconDownload size={14} />}
                        onClick={() => downloadFile(`candidates/${candidateId}/sources/${source.id}/download`)}
                      >
                        {t("download")}
                      </Button>
                    )}
                    <Button
                      variant="subtle" size="xs" color="red"
                      onClick={() => confirmDeleteSource(source)}
                      aria-label={t("delete")}
                    >
                      <IconTrash size={14} />
                    </Button>
                  </Group>
                </Group>
                {isExpanded && (
                  <Card mt="sm" p="sm" bg="gray.0" style={{ maxHeight: 300, overflow: "auto" }}>
                    <Text size="xs" style={{ whiteSpace: "pre-wrap", fontFamily: "monospace" }}>
                      {source.sourceType === "linkedIn" && source.rawContent
                        ? source.rawContent
                        : (source.extractedText || source.rawContent)}
                    </Text>
                  </Card>
                )}
                {isTranscript && (
                  <TranscriptAnalysisPanel
                    candidateId={candidateId} source={source} onSourcesChanged={onSourcesChanged}
                  />
                )}
              </Card>
            );
          })}
        </Stack>
      )}
      <WebFindingsCard sources={sources} candidate={candidate} candidateId={candidateId}
        onSourcesChanged={onSourcesChanged} onEnrich={openEnrichModal} />
    </Stack>
  );
}

export default function CandidateProfile({ params }) {
  const { t, i18n } = useTranslation("Veft");
  const [, setLocation] = useLocation();
  const search = useSearch();
  const initialTab = new URLSearchParams(search).get("tab") === "background" ? "background" : "timeline";
  const [tab, setTab] = useState(initialTab);
  const nameFormat = useStore((s) => s.nameFormat);
  const [candidate, setCandidate] = useState(null);
  const [activities, setActivities] = useState([]);
  const [sources, setSources] = useState([]);
  const [tagDefs, setTagDefs] = useState([]);
  const [projectLinks, setProjectLinks] = useState([]);
  const [clientLinks, setClientLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addProjectOpen, setAddProjectOpen] = useState(false);
  const [projectsList, setProjectsList] = useState([]);
  const [projectsLoading, setProjectsLoading] = useState(false);
  const [selectedProjectId, setSelectedProjectId] = useState(null);
  const [addingProject, setAddingProject] = useState(false);
  const [typeFilter, setTypeFilter] = useState(null);
  const [showSystem, setShowSystem] = useState(false);
  const [editingHeader, setEditingHeader] = useState(false);
  const [headerDraft, setHeaderDraft] = useState({});
  const [savingHeader, setSavingHeader] = useState(false);
  const isMobile = useIsMobile();

  useEffect(() => {
    setLoading(true);
    loadData().finally(() => setLoading(false));
  }, [params.id]);

  // Poll while an enrichment job is running so the user sees status updates
  // and newly-created sources without having to reload the page.
  useEffect(() => {
    if (!candidate) return;
    const status = candidate.enrichmentStatus;
    const inProgress = status && status !== "none" && status !== "complete" && status !== "failed";
    if (!inProgress) return;

    const interval = setInterval(async () => {
      try {
        const [cand, srcs] = await Promise.all([
          getCandidate(params.id),
          getCandidateSources(params.id),
        ]);
        setCandidate(cand);
        setSources(Array.isArray(srcs) ? srcs : []);
      } catch (e) {
        console.warn("enrichment polling failed", e);
      }
    }, 3000);

    return () => clearInterval(interval);
  }, [candidate?.enrichmentStatus, params.id]);

  // Refreshes the data in place without toggling the page-level loader.
  // The mount effect above wraps the first call with setLoading; subsequent
  // refreshes (refetch LinkedIn, source uploads, polling) keep the rendered
  // tabs mounted so the user's active tab and scroll position survive.
  async function loadData() {
    try {
      const [cand, acts, srcs, tags, projLinks, cliLinks] = await Promise.all([
        getCandidate(params.id),
        getActivities(params.id),
        getCandidateSources(params.id),
        getTagDefinitions().catch(() => []),
        getCandidateProjects(params.id).catch(() => []),
        getCandidateClientLinks(params.id).catch(() => []),
      ]);
      setCandidate(cand);
      setActivities(Array.isArray(acts) ? acts : []);
      setSources(Array.isArray(srcs) ? srcs : []);
      setTagDefs(Array.isArray(tags) ? tags : []);
      setProjectLinks(Array.isArray(projLinks) ? projLinks : []);
      setClientLinks(Array.isArray(cliLinks) ? cliLinks : []);
    } catch (e) {
      console.error("Failed to load candidate", e);
    }
  }

  async function openAddProject() {
    setSelectedProjectId(null);
    setAddProjectOpen(true);
    if (projectsList.length === 0) {
      setProjectsLoading(true);
      try {
        const ps = await getProjects();
        setProjectsList(Array.isArray(ps) ? ps : []);
      } catch {
        setProjectsList([]);
      } finally {
        setProjectsLoading(false);
      }
    }
  }

  async function handleAddToProject() {
    if (!selectedProjectId) return;
    setAddingProject(true);
    try {
      await addProjectCandidate(selectedProjectId, { candidateId: candidate.id });
      notifications.show({ title: t("candidate_added"), message: t("added_to_project_msg") });
      setAddProjectOpen(false);
      const projLinks = await getCandidateProjects(params.id).catch(() => []);
      setProjectLinks(Array.isArray(projLinks) ? projLinks : []);
    } catch {
      notifications.show({ color: "red", title: t("error"), message: t("add_candidate_failed") });
    } finally {
      setAddingProject(false);
    }
  }

  function handleActivityCreated(activity) {
    if (activity) {
      setActivities((prev) => [activity, ...prev]);
    }
  }

  function handleActivityUpdated(updated) {
    setActivities((prev) => prev.map((a) => (a.id === updated.id ? updated : a)));
  }

  function handleActivityDeleted(id) {
    setActivities((prev) => prev.filter((a) => a.id !== id));
  }

  // Refresh the activity timeline after a manual field edit so the change-tracking (FieldChange)
  // entries the edit just produced appear live, not only on the next page load.
  function refreshActivities() {
    getActivities(params.id).then((a) => setActivities(Array.isArray(a) ? a : [])).catch(() => {});
  }
  function onCandidateEdited(c) {
    setCandidate(c);
    refreshActivities();
  }

  if (loading) {
    return <Center py="xl"><Loader /></Center>;
  }

  if (!candidate) {
    return <Text>{t("candidate_not_found")}</Text>;
  }

  // Headline is DERIVED (a consistent role+company pair) — never edited directly in the header pencil,
  // which would mix sources. Correct it by editing the current (top-most) career row or re-enriching:
  // editing the top row re-points the derived pair server-side so the change shows here. See candidateRole.js.
  const { role: headlineRole, company: headlineCompany, isFormer: headlineFormer } = candidateRoleAndCompany(candidate);
  // Mark a past role with a localized "prev."/"ehem." prefix when every career period has ended.
  const headlineRoleLabel = headlineFormer && headlineRole ? `${t("role_former_prefix")} ${headlineRole}` : headlineRole;
  const filteredActivities = typeFilter
    ? activities.filter((a) => a.type === typeFilter)
    : activities.filter((a) => showSystem || !isSystemEvent(a.type));

  function startEditHeader() {
    setHeaderDraft({
      firstName: candidate.firstName ?? "",
      lastName: candidate.lastName ?? "",
      // Role + company are NOT edited here — the headline is derived (see candidateRoleAndCompany);
      // correct it via the career list or re-enrichment. The pencil edits only non-derived fields.
      location: candidate.geoLocation?.displayName ?? candidate.locationDisplay ?? candidate.location ?? "",
      geoLocation: candidate.geoLocation ?? null,
      linkedInUrl: candidate.linkedInUrl ?? "",
    });
    setEditingHeader(true);
  }

  async function saveHeader() {
    setSavingHeader(true);
    try {
      const dto = {
        firstName: headerDraft.firstName.trim(),
        lastName: headerDraft.lastName.trim(),
        linkedInUrl: headerDraft.linkedInUrl.trim() || null,
      };

      // Only touch location/geoLocation when the user actually changed them — otherwise an unrelated
      // header edit (e.g. just the title) would overwrite the raw source-of-truth Location with the
      // geocoded display name. Seed equals the header-render precedence (see startEditHeader).
      const seededLocation = candidate.geoLocation?.displayName ?? candidate.locationDisplay ?? candidate.location ?? "";
      const locationChanged =
        headerDraft.location.trim() !== seededLocation.trim() ||
        (headerDraft.geoLocation?.placeId ?? null) !== (candidate.geoLocation?.placeId ?? null);
      if (locationChanged) {
        dto.location = headerDraft.location.trim() || null;
        dto.geoLocation = headerDraft.geoLocation ?? null;
      }

      const updated = await updateCandidate(candidate.id, dto);
      setCandidate(updated);
      refreshActivities();
      setEditingHeader(false);
    } catch {
      notifications.show({ color: "red", title: t("error"), message: t("error") });
    } finally {
      setSavingHeader(false);
    }
  }

  async function handleWatchlistToggle(checked) {
    try {
      const updated = await setCandidateWatchlist(candidate.id, checked);
      setCandidate(updated);
    } catch {
      notifications.show({ color: "red", title: t("error"), message: t("error") });
    }
  }

  function handleDelete() {
    modals.openConfirmModal({
      title: t("delete_candidate"),
      centered: true,
      children: (
        <Text size="sm">
          {t("delete_confirm", { name: candidate.name })}
        </Text>
      ),
      labels: { confirm: t("delete"), cancel: t("cancel") },
      confirmProps: { color: "red" },
      onConfirm: async () => {
        try {
          await deleteCandidate(candidate.id);
          notifications.show({ title: t("deleted_notification"), message: t("candidate_deleted_msg", { name: candidate.name }), color: "green" });
          setLocation("/candidates");
        } catch {
          notifications.show({ color: "red", title: t("error"), message: t("error") });
        }
      },
    });
  }

  function handleFindSimilar() {
    setLocation(`/search?similarTo=${candidate.id}`);
  }

  const titleSuffix = headlineCompany ? ` ${t("at_company", { company: headlineCompany })}` : "";

  // On a phone the actions sit inline on the name row, so every visible icon steals name width:
  // LinkedIn folds into the overflow menu there and only the star + ⋮ stay visible.
  const headerActions = (
    <Group gap={4} wrap="nowrap">
      <Tooltip label={t("role_interestingPotential")}>
        <ActionIcon
          variant="subtle"
          color={candidate.isWatchlist ? "yellow" : "gray"}
          onClick={() => handleWatchlistToggle(!candidate.isWatchlist)}
          aria-label={t("role_interestingPotential")}
        >
          {candidate.isWatchlist ? <IconStarFilled size={16} /> : <IconStar size={16} />}
        </ActionIcon>
      </Tooltip>
      {candidate.linkedInUrl && !isMobile && (
        <Tooltip label="LinkedIn">
          <ActionIcon
            variant="subtle"
            color="blue"
            component="a"
            href={candidate.linkedInUrl}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="LinkedIn"
          >
            <IconBrandLinkedin size={16} />
          </ActionIcon>
        </Tooltip>
      )}
      <Menu position="bottom-end" withinPortal>
        <Menu.Target>
          <ActionIcon variant="subtle" color="gray" aria-label={t("more_actions")}>
            <IconDotsVertical size={16} />
          </ActionIcon>
        </Menu.Target>
        <Menu.Dropdown>
          {candidate.linkedInUrl && isMobile && (
            <Menu.Item leftSection={<IconBrandLinkedin size={14} />} component="a"
              href={candidate.linkedInUrl} target="_blank" rel="noopener noreferrer">
              LinkedIn
            </Menu.Item>
          )}
          <Menu.Item leftSection={<IconSearch size={14} />} onClick={handleFindSimilar}>
            {t("find_similar")}
          </Menu.Item>
          <Menu.Item leftSection={<IconShieldSearch size={14} />} onClick={() => setTab("background")}>
            {t("bg_title")}
          </Menu.Item>
          <Menu.Divider />
          <Menu.Item color="red" leftSection={<IconTrash size={14} />} onClick={handleDelete}>
            {t("delete")}
          </Menu.Item>
        </Menu.Dropdown>
      </Menu>
    </Group>
  );

  return (
    <Container size="lg" py="md">
    <Stack gap="lg">
      <Group>
        {/* Return to wherever the user came from — search results, a project, the list — instead of
            always dumping them on the contact list. Most users don't distinguish the app's back link
            from the browser's, so mirror the browser: fall back to the list only when there's no
            in-app history to return to (profile opened via a direct link or a fresh tab). */}
        <Button
          variant="subtle"
          size="xs"
          leftSection={<IconArrowLeft size={14} />}
          onClick={() => (window.history.length > 1 ? window.history.back() : setLocation("/candidates"))}
        >
          {t("back")}
        </Button>
      </Group>

      {/* Header */}
      <Card>
        {editingHeader ? (
          <Stack gap="sm">
            <Group justify="space-between">
              <Title order={5}>{t("edit_profile")}</Title>
              <Group gap="xs">
                <Button size="xs" variant="subtle" color="gray" onClick={() => setEditingHeader(false)}>{t("cancel")}</Button>
                <Button size="xs" loading={savingHeader} onClick={saveHeader}>{t("save")}</Button>
              </Group>
            </Group>
            <Group grow>
              <TextInput label={t("first_name")} value={headerDraft.firstName}
                onChange={(e) => setHeaderDraft((d) => ({ ...d, firstName: e.target.value }))} />
              <TextInput label={t("last_name")} value={headerDraft.lastName}
                onChange={(e) => setHeaderDraft((d) => ({ ...d, lastName: e.target.value }))} />
            </Group>
            <GeoLocationAutocomplete value={headerDraft.location}
              onChange={({ location, geoLocation }) => setHeaderDraft((d) => ({ ...d, location, geoLocation }))} />
            <TextInput label={t("linkedin_url")} value={headerDraft.linkedInUrl}
              onChange={(e) => setHeaderDraft((d) => ({ ...d, linkedInUrl: e.target.value }))} />
            {(() => {
              // Same tripwire as the URL modal, against the DRAFT names — the user may be fixing
              // both the name and the URL in one edit.
              const slugName = linkedInSlugMismatch(headerDraft.linkedInUrl, {
                firstName: headerDraft.firstName, lastName: headerDraft.lastName, name: candidate.name,
              });
              return slugName ? (
                <Text size="xs" c="orange">
                  {t("linkedin_slug_warning", {
                    slugName,
                    candidateName: `${headerDraft.firstName} ${headerDraft.lastName}`.trim() || candidate.name,
                  })}
                </Text>
              ) : null;
            })()}
            <EmailsEditor candidate={candidate} onUpdated={onCandidateEdited} />
            <PhoneNumbersEditor candidate={candidate} onUpdated={onCandidateEdited} />
            <AddressesEditor candidate={candidate} onUpdated={onCandidateEdited} />
            <TagsEditor candidate={candidate} tagDefs={tagDefs} onUpdated={onCandidateEdited} />
          </Stack>
        ) : isMobile ? (
          <Stack gap="sm">
            <Group gap="sm" wrap="nowrap" align="flex-start">
              <EditableCandidateAvatar candidate={candidate} onUpdated={onCandidateEdited}
                size="lg" radius="xl" color="blue" style={{ flexShrink: 0 }} />
              {/* Actions share the name row instead of a reserved right column, so the role and
                  location lines get the full remaining width and rarely need to clamp. */}
              <Box style={{ minWidth: 0, flex: 1 }}>
                <Group gap={4} wrap="nowrap">
                  <Title order={4} style={{ minWidth: 0 }} truncate>{formatCandidateName(candidate, nameFormat)}</Title>
                  <ActionIcon size="xs" variant="subtle" color="gray" style={{ flexShrink: 0 }} onClick={startEditHeader}>
                    <IconPencil size={12} />
                  </ActionIcon>
                  <Box ml="auto" style={{ flexShrink: 0 }}>{headerActions}</Box>
                </Group>
                <Text size="sm" c="dimmed" lineClamp={2}>{headlineRoleLabel}{titleSuffix}</Text>
                {candidateLocation(candidate) && <Text size="xs" c="dimmed">{candidateLocation(candidate)}</Text>}
              </Box>
            </Group>
            <ContactLines candidate={candidate} compact />
            <Group gap="xs" align="center">
              {candidate.computedStatus && candidate.computedStatus !== "none" && (
                <Badge size="xs" variant="filled" color={statusColors[candidate.computedStatus] ?? "gray"}>
                  {t(`role_${candidate.computedStatus}`)}
                </Badge>
              )}
              {(candidate.roles ?? [])
                .filter((r) => !(r === "candidate" && candidate.computedStatus && candidate.computedStatus !== "none"))
                .map((r) => (
                  <Badge key={r} size="xs" variant="dot" color={roleColors[r] ?? "gray"}>{t(`role_${r}`)}</Badge>
                ))}
            </Group>
            {[...new Set((candidate.languages ?? []).map(toIsoCode))].length > 0 && (
              <Group gap={4}>
                {[...new Set((candidate.languages ?? []).map(toIsoCode))].map((code) => (
                  <Badge key={code} size="xs" variant="outline" color="grape">{languageDisplayLabel(code, i18n.language)}</Badge>
                ))}
              </Group>
            )}
            <ExpandableTags tags={candidate.tags} limit={6} />
            <SourcedByControl candidate={candidate} onUpdated={onCandidateEdited} />
          </Stack>
        ) : (
          <Group justify="space-between" align="flex-start" wrap="nowrap">
            <Group align="flex-start" style={{ minWidth: 0, flex: 1 }}>
              <EditableCandidateAvatar candidate={candidate} onUpdated={onCandidateEdited}
                size="xl" radius="xl" color="blue" />
              <div style={{ minWidth: 0, flex: 1 }}>
                <Group gap="xs">
                  <Title order={3}>{formatCandidateName(candidate, nameFormat)}</Title>
                  <ActionIcon size="sm" variant="subtle" color="gray" onClick={startEditHeader}>
                    <IconPencil size={14} />
                  </ActionIcon>
                </Group>
                <Text c="dimmed">{headlineRoleLabel}{titleSuffix}</Text>
                <Text size="sm" c="dimmed">{candidateLocation(candidate)}</Text>
                <Group gap="xs" mt="xs" align="center">
                  {candidate.computedStatus && candidate.computedStatus !== "none" && (
                    <Badge size="xs" variant="filled" color={statusColors[candidate.computedStatus] ?? "gray"}>
                      {t(`role_${candidate.computedStatus}`)}
                    </Badge>
                  )}
                  {(candidate.roles ?? [])
                    .filter((r) => !(r === "candidate" && candidate.computedStatus && candidate.computedStatus !== "none"))
                    .map((r) => (
                      <Badge key={r} size="xs" variant="dot" color={roleColors[r] ?? "gray"}>{t(`role_${r}`)}</Badge>
                    ))}
                </Group>
                {[...new Set((candidate.languages ?? []).map(toIsoCode))].length > 0 && (
                  <Group gap={4} mt="xs">
                    {[...new Set((candidate.languages ?? []).map(toIsoCode))].map((code) => (
                      <Badge key={code} size="xs" variant="outline" color="grape">{languageDisplayLabel(code, i18n.language)}</Badge>
                    ))}
                  </Group>
                )}
                {(candidate.tags ?? []).length > 0 && (
                  <Box mt="xs"><ExpandableTags tags={candidate.tags} limit={4} /></Box>
                )}
                <Box mt="xs"><SourcedByControl candidate={candidate} onUpdated={onCandidateEdited} /></Box>
              </div>
            </Group>
            <Stack gap="xs" align="flex-end" style={{ flexShrink: 0 }}>
              {headerActions}
              <ContactLines candidate={candidate} />
            </Stack>
          </Group>
        )}
      </Card>

      {projectLinks.some((pl) => isActiveStage(pl.stage)) && (
        <Group gap="xs">
          {projectLinks.filter((pl) => isActiveStage(pl.stage)).map((pl) => (
            <Badge
              key={pl.id}
              size="sm"
              variant="light"
              color={stageColors[pl.stage] ?? "gray"}
              style={{ cursor: "pointer", textTransform: "none" }}
              onClick={() => setLocation(`/projects/${pl.projectId}`)}
            >
              {pl.projectTitle} · {t(`stage_${pl.stage}`)}
            </Badge>
          ))}
        </Group>
      )}

      <Tabs value={tab} onChange={(v) => v && setTab(v)}>
        <ScrollableTabsList>
          <Tabs.Tab value="timeline" leftSection={<IconHistory size={14} />}>{t("tab_timeline")} ({activities.length})</Tabs.Tab>
          <Tabs.Tab value="career" leftSection={<IconBriefcase size={14} />}>{t("tab_career")}</Tabs.Tab>
          <Tabs.Tab value="documents" leftSection={<IconFileText size={14} />}>{t("tab_documents")} ({sources.length})</Tabs.Tab>
          <Tabs.Tab value="projects" leftSection={<IconFolders size={14} />}>{t("tab_projects")} ({projectLinks.length})</Tabs.Tab>
          <Tabs.Tab value="background" leftSection={<IconShieldSearch size={14} />}>{t("tab_background")}</Tabs.Tab>
          <Tabs.Tab value="tasks" leftSection={<IconListCheck size={14} />}>{t("tab_tasks")}</Tabs.Tab>
        </ScrollableTabsList>

        {/* Timeline Tab */}
        <Tabs.Panel value="timeline" pt="md">
          <QuickAdd candidateId={candidate.id} onCreated={handleActivityCreated} />

          {/* Filter bar — the type chips collapse into a "Filter" drawer on a phone so they don't
              wall off the timeline; the show-events toggle stays pinned on the right. */}
          <Box mb="md">
            <FilterBar
              activeCount={typeFilter ? 1 : 0}
              trailing={
                <Switch
                  size="xs"
                  label={t("show_events")}
                  checked={showSystem}
                  onChange={(e) => {
                    const checked = e.currentTarget.checked;
                    setShowSystem(checked);
                    if (!checked && isSystemEvent(typeFilter)) setTypeFilter(null);
                  }}
                />
              }
            >
              <Group gap="xs">
                <Badge
                  size="sm"
                  variant={typeFilter === null ? "filled" : "light"}
                  style={{ cursor: "pointer" }}
                  onClick={() => setTypeFilter(null)}
                >
                  {t("all_filter")}
                </Badge>
                {Object.entries(entryTypeConfig).map(([key, cfg]) => {
                  if (isSystemEvent(key) && !showSystem) return null;
                  const count = activities.filter((a) => a.type === key).length;
                  if (count === 0) return null;
                  return (
                    <Badge
                      key={key}
                      size="sm"
                      color={cfg.color}
                      variant={typeFilter === key ? "filled" : "light"}
                      style={{ cursor: "pointer" }}
                      onClick={() => setTypeFilter(typeFilter === key ? null : key)}
                    >
                      {t(cfg.tKey)} ({count})
                    </Badge>
                  );
                })}
              </Group>
            </FilterBar>
          </Box>

          {filteredActivities.length === 0 && !(typeFilter === null && candidate.notes) ? (
            <Text size="sm" c="dimmed">{typeFilter ? t("no_entries_of_type") : t("no_timeline_entries")}</Text>
          ) : (
            filteredActivities.map((entry) => (
              <TimelineEntry key={entry.id} entry={entry} candidateId={candidate.id}
                onUpdated={handleActivityUpdated} onDeleted={handleActivityDeleted} showTime={showSystem} />
            ))
          )}

          {/* Imported Outlook note — the origin of the record. Read-only artifact pinned below the
              attributed history; only in the unfiltered "all" view (it isn't a typed activity). */}
          {typeFilter === null && <ImportedNoteCard notes={candidate.notes} />}
        </Tabs.Panel>

        {/* Career Tab */}
        <Tabs.Panel value="career" pt="md">
          <CareerTab candidate={candidate} activities={activities} sources={sources}
            onUpdated={onCandidateEdited} onRefresh={loadData} />
        </Tabs.Panel>

        {/* Documents Tab */}
        <Tabs.Panel value="documents" pt="md">
          <DocumentsTab candidate={candidate} sources={sources} onSourcesChanged={loadData} />
        </Tabs.Panel>

        {/* Projects Tab */}
        <Tabs.Panel value="projects" pt="md">
          <Group justify="flex-end" mb="md">
            <Button size="xs" leftSection={<IconPlus size={14} />} onClick={openAddProject}>
              {t("add_to_project")}
            </Button>
          </Group>
          {clientLinks.length > 0 && (
            <Stack gap="xs" mb="md">
              <Text size="sm" fw={500}>{t("clients")}</Text>
              {clientLinks.map((cl, i) => (
                <Card key={i} p="sm" withBorder style={{ cursor: "pointer" }}
                  onClick={() => setLocation(`/clients/${cl.clientId}`)}>
                  <Group gap="xs">
                    <ThemeIcon variant="light" size="sm"><IconBuilding size={14} /></ThemeIcon>
                    <div>
                      <Text size="sm" fw={500}>{cl.clientName}</Text>
                      {cl.contactRole && <Text size="xs" c="dimmed">{cl.contactRole}</Text>}
                    </div>
                  </Group>
                </Card>
              ))}
              <Divider />
            </Stack>
          )}
          {projectLinks.length === 0 && clientLinks.length === 0 ? (
            <Text size="sm" c="dimmed">{t("not_linked")}</Text>
          ) : projectLinks.length === 0 ? null : (
            <Stack gap="sm">
              {[...projectLinks].sort((a, b) => (stageOrder[a.stage] ?? 99) - (stageOrder[b.stage] ?? 99)).map((pl) => (
                <Card key={pl.id} p="sm" withBorder style={{ cursor: "pointer" }}
                  onClick={() => setLocation(`/projects/${pl.projectId}`)}>
                  <Group justify="space-between" wrap="nowrap">
                    <div style={{ minWidth: 0 }}>
                      <Text size="sm" fw={500}>{pl.projectTitle}</Text>
                      {pl.roleTitle && <Text size="xs" c="dimmed">{pl.roleTitle}</Text>}
                    </div>
                    <Group gap="xs" style={{ flexShrink: 0 }}>
                      <Badge size="xs" variant="light" color={stageColors[pl.stage] ?? "gray"}>
                        {t(`stage_${pl.stage}`)}
                      </Badge>
                      {pl.ranking && (
                        <Badge size="xs" variant="filled" color="dark">{pl.ranking}</Badge>
                      )}
                    </Group>
                  </Group>
                </Card>
              ))}
            </Stack>
          )}
        </Tabs.Panel>

        <Tabs.Panel value="background" pt="md">
          {tab === "background" && <BackgroundCheckPanel key={candidate.id} candidateId={candidate.id} />}
        </Tabs.Panel>

        <Tabs.Panel value="tasks" pt="md">
          <TasksPanel candidateId={candidate.id} />
        </Tabs.Panel>
      </Tabs>

      <Modal
        opened={addProjectOpen}
        onClose={() => { if (!addingProject) setAddProjectOpen(false); }}
        title={t("add_to_project")}
      >
        <Stack>
          <Select
            data={projectsList
              .filter((p) => !projectLinks.some((pl) => pl.projectId === p.id))
              .map((p) => ({ value: p.id, label: p.title }))}
            value={selectedProjectId}
            onChange={setSelectedProjectId}
            placeholder={t("select_project")}
            nothingFoundMessage={projectsLoading ? null : t("no_projects")}
            rightSection={projectsLoading ? <Loader size={14} /> : null}
            searchable
            clearable
            disabled={projectsLoading || addingProject}
          />
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setAddProjectOpen(false)} disabled={addingProject}>
              {t("cancel")}
            </Button>
            <Button
              onClick={handleAddToProject}
              disabled={!selectedProjectId || addingProject}
              loading={addingProject}
            >
              {t("add")}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
    </Container>
  );
}
